import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, PencilSimple, Trash, Users, UsersThree, CalendarBlank, Calendar, Clock, MapPin, CheckCircle, XCircle, Confetti, Warning, Lock, SealCheck, ListBullets, Trophy, MagnifyingGlass, ArrowsClockwise, Receipt, ArrowUp } from '@phosphor-icons/react';
import MemberBadge from '../components/MemberBadge';
import MixinLineupBoard from '../components/MixinLineupBoard';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { formatPrice } from '../lib/price';
import { MixinSettleDialog } from '../components/MixinSettleDialog';
import { PaymentMethodPicker } from '../components/PaymentMethodPicker';
import { SignupConfirmDialog } from '../components/SignupConfirmDialog';
import { useCardPayment } from '../lib/cardTerminal';

const Community = () => {
  const { user, language, API } = useAuth();
  const navigate = useNavigate();
  // Treat anyone with the `manages_community` permission flag as an
  // admin for Mixin management purposes. Fabrizio (role=trainer) has
  // this flag set so he can run Mixins end-to-end just like admin/staff.
  const isAdminOrStaff = user?.role === 'admin' || user?.role === 'staff' || !!user?.manages_community;
  const L = (en, deu) => language === 'de' ? deu : en;
  const dateLocale = language === 'de' ? de : enUS;

  const [events, setEvents] = useState([]);
  // Admin-only: finalized events that still have open pay-at-club balances
  // or open customer-tabs. Reception settles them before closing.
  const [pendingCloseEvents, setPendingCloseEvents] = useState([]);
  const [settleFor, setSettleFor] = useState(null);
  const [payPick, setPayPick] = useState(null);
  const [confirmSignup, setConfirmSignup] = useState(null);
  const payByCard = useCardPayment();
  const [expandedPending, setExpandedPending] = useState({});
  const [loading, setLoading] = useState(true);
  // Prevent background re-fetches from blanking the page (Oct 2026): the
  // initial load must show the big "Loading..." placeholder, but every
  // subsequent fetch (even non-silent) should keep the current UI mounted
  // so the user's scroll position stays put.
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [lateFeeConfirm, setLateFeeConfirm] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'event'|'series'|'player', ev, userId, userName, message }
  const [lineupModal, setLineupModal] = useState(null); // { event, mode: 'lineup'|'results' }
  const [partnerSignup, setPartnerSignup] = useState(null); // { event, mode, partner, payForBoth }
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [partnerSearching, setPartnerSearching] = useState(false);
  // Payment-detail modal opened by clicking the amber "Zahlung" badge on
  // a participant with `payment_status === 'awaiting_payment'`. Lets admins
  // see the Datatrans state and force-reconcile without leaving the page.
  const [paymentInfo, setPaymentInfo] = useState(null); // { loading, event, participant, transaction, error }
  const [reconciling, setReconciling] = useState(false);

  const openPaymentInfo = useCallback(async (ev, p) => {
    setPaymentInfo({ loading: true, event: ev, participant: p });
    try {
      const { data } = await API.get(`/admin/community/events/${ev.id}/participants/${p.user_id}/payment-info`);
      setPaymentInfo({ loading: false, ...data });
    } catch (e) {
      setPaymentInfo({ loading: false, error: e?.response?.data?.detail || e.message, event: ev, participant: p });
    }
  }, [API]);

  // Open a walk-in customer tab for this Mixin participant. Reuses any
  // still-open tab for the same (customer, event) so extra rounds land on
  // the same bill. Admins land on the standard tab UI where drinks / rackets
  // / rentals can be added and paid — the amounts flow into the Kasse
  // exactly like a court-tab payment.
  const openParticipantTab = useCallback(async (ev, participant) => {
    if (!isAdminOrStaff || !participant?.user_id) return;
    try {
      const { data } = await API.post('/tabs/customer', {
        customer_id: participant.user_id,
        context_type: 'mixin',
        context_id: ev.id,
        context_label: `${ev.title || 'Mixin'} · ${participant.user_name || ''}`.trim().replace(/·\s*$/, ''),
      });
      if (data?.id) {
        navigate(`/community?open_event=${ev.id}`, { replace: true });
        navigate(`/admin/tabs/${data.id}?return_to=${encodeURIComponent(`/community?open_event=${ev.id}`)}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || L('Failed to open tab', 'Konto konnte nicht geöffnet werden'));
    }
  }, [API, isAdminOrStaff, navigate, L]);

  const runReconcile = useCallback(async () => {
    if (!paymentInfo?.event?.id || !paymentInfo?.participant?.user_id) return;
    setReconciling(true);
    try {
      const { data } = await API.post(`/admin/community/events/${paymentInfo.event.id}/participants/${paymentInfo.participant.user_id}/reconcile`);
      toast.success(`${L('Reconciled', 'Abgeglichen')}: ${data.participant_payment_status || data.transaction_status || '—'}`);
      // Refresh the modal + the events list so the badge disappears.
      await openPaymentInfo(paymentInfo.event, paymentInfo.participant);
      if (typeof loadEvents === 'function') loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setReconciling(false);
    }
  }, [API, paymentInfo, openPaymentInfo, L]);

  const emptyForm = useMemo(() => ({
    type: 'mixin',
    title: '',
    date: '',
    start_time: '20:00',
    duration_minutes: 120,
    num_courts: 8,
    max_participants: 32,
    price_per_player: 0,
    description: '',
    weekly_recurring: false,
    weekly_end_date: '',
    signup_opens_weekday: '',
    signup_opens_time: '',
    cancellation_cutoff_hours: 6,
    players_per_court: 4,
    signup_opens_at: '',
    members_only: false,
    signup_mode: 'solo',  // 'solo' | 'partner' | 'both'
    member_price: '',
    pay_at_club_only: false,
    min_level: '',  // '' means no lower bound
    max_level: '',  // '' means no upper bound
  }), []);
  const [form, setForm] = useState(emptyForm);

  const loadEvents = useCallback(async (opts = {}) => {
    // Silent refresh (used by background pollers / auto-refresh loops)
    // doesn't flip `loading=true`, so the event list stays mounted and
    // the user's scroll position is preserved. Only the very first load
    // + explicit user actions show the skeleton.
    const silent = opts && opts.silent === true;
    try {
      if (!silent) setLoading(true);
      // Admin/Staff: pending-close section loads in parallel and never blocks the main list.
      if (isAdminOrStaff) {
        API.get('/admin/community/events/pending-close')
          .then(pc => setPendingCloseEvents(pc.data || []))
          .catch(() => { /* ignore */ });
      }
      const { data } = await API.get('/community/events?upcoming_only=true');
      setEvents(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { if (!silent) setLoading(false); setInitialLoaded(true); }
  }, [API, language, isAdminOrStaff]);

  const closeEvent = async (ev, { force = false } = {}) => {
    try {
      await API.post(`/admin/community/events/${ev.id}/close${force ? '?force=true' : ''}`);
      toast.success(L('Event closed', 'Event abgeschlossen'));
      loadEvents({ silent: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.code === 'event_has_open_balances') {
        const msg = L(
          `Still open: ${detail.unpaid_at_club} pay-at-club, ${detail.open_tabs} customer-tab(s). Close anyway?`,
          `Noch offen: ${detail.unpaid_at_club} „im Club offen", ${detail.open_tabs} Kunden-Konto(s). Trotzdem abschließen?`,
        );
        if (window.confirm(msg)) return closeEvent(ev, { force: true });
      } else {
        toast.error(detail || L('Error', 'Fehler'));
      }
    }
  };

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Ticking clock (1 Hz). Drives live signup-opens countdowns without polling
  // the server. When any event's `signup_opens_at` transitions past `now`, we
  // fire a single loadEvents() to refresh the button state — so users don't
  // need to hit F5 at 11:00 sharp.
  const [now, setNow] = useState(() => Date.now());
  // Only tick every second while a signup countdown is actually running
  // (opens within the next 24h); otherwise once a minute — avoids re-rendering
  // the whole page every second.
  const countdownActive = (events || []).some(ev => {
    if (ev.signup_is_open !== false || !(ev.my_signup_opens_at || ev.signup_opens_at)) return false;
    const ms = new Date((ev.my_signup_opens_at || ev.signup_opens_at)).getTime() - Date.now();
    return ms > -5000 && ms < 24 * 3600 * 1000;
  });
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), countdownActive ? 1000 : 60000);
    return () => clearInterval(t);
  }, [countdownActive]);

  // Track which events we've already auto-refreshed so we only trigger a
  // reload once per event when its signup opens.
  const autoRefreshedRef = React.useRef(new Set());
  useEffect(() => {
    if (!events?.length) return;
    let shouldReload = false;
    for (const ev of events) {
      if (ev.signup_is_open === false && (ev.my_signup_opens_at || ev.signup_opens_at)) {
        const openMs = new Date((ev.my_signup_opens_at || ev.signup_opens_at)).getTime();
        if (Number.isFinite(openMs) && openMs <= now && !autoRefreshedRef.current.has(ev.id)) {
          autoRefreshedRef.current.add(ev.id);
          shouldReload = true;
        }
      }
    }
    if (shouldReload) loadEvents({ silent: true });
  }, [now, events, loadEvents]);

  // Datatrans success-redirect handler: when ?signup_paid=1 is in URL,
  // poll the backend to flip pending → paid and refresh the event list.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wtx = params.get('wtx');
    const paid = params.get('signup_paid') || params.get('late_fee');
    if (wtx && paid) {
      (async () => {
        try {
          await API.get(`/payments/status/${wtx}`);
          toast.success(paid === 'paid'
            ? L('Late fee paid — thanks!', 'Late-Gebühr bezahlt — danke!')
            : L('Signup confirmed — see you on court!', 'Anmeldung bestätigt — bis bald auf dem Court!'));
        } catch (_) { /* ignore */ }
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        loadEvents();
      })();
    }
  }, [API, loadEvents, language]);

  // On mount + whenever the user's own events change, silently reconcile
  // any pending Datatrans transactions they own. This closes the "I just
  // paid but the banner still says 'Zahlung erforderlich'" gap: instead
  // of waiting up to 60s for the background sweeper, we poll Datatrans
  // right after the page loads so the banner disappears within a second.
  const selfReconciledRef = React.useRef({ lastAt: 0 });
  useEffect(() => {
    if (!user) return;
    // Debounce: only run once per 10s so it doesn't spam Datatrans on
    // rapid re-renders.
    if (Date.now() - selfReconciledRef.current.lastAt < 10_000) return;
    // Skip if the user has zero awaiting_payment entries — no point in polling.
    const hasPending = (events || []).some(ev =>
      (ev.participants || []).some(p => p.user_id === user.id && p.payment_status === 'awaiting_payment')
    );
    if (!hasPending) return;
    selfReconciledRef.current.lastAt = Date.now();
    (async () => {
      try {
        const { data } = await API.post('/community/my/reconcile-pending');
        if ((data?.reconciled_paid || 0) > 0) {
          toast.success(L('Payment confirmed — you\'re in!', 'Zahlung bestätigt — du bist dabei!'));
          loadEvents({ silent: true });
        }
      } catch (_) { /* silent */ }
    })();
  }, [events, user, API, L, loadEvents]);

  useEffect(() => {
    if (!showAddPlayer || searchQuery.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get(`/users/search?q=${encodeURIComponent(searchQuery)}&limit=10`);
        setSearchResults(data || []);
      } catch (_) { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, showAddPlayer, API]);

  // Partner autocomplete (customer-safe endpoint — no PII leak)
  useEffect(() => {
    if (!partnerSignup || partnerQuery.trim().length < 2) { setPartnerResults([]); return; }
    setPartnerSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get(`/users/partner-search?q=${encodeURIComponent(partnerQuery)}&limit=10`);
        setPartnerResults(data || []);
      } catch (_) { setPartnerResults([]); }
      finally { setPartnerSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [partnerQuery, partnerSignup, API]);

  const openCreate = () => { setEditingEvent(null); setForm(emptyForm); setShowModal(true); };

  // Helpers to convert between backend ISO datetime and the HTML
  // `datetime-local` input format (YYYY-MM-DDTHH:MM, treated as local time).
  const isoToLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const localInputToIso = (s) => {
    if (!s) return null;
    const d = new Date(s); // browser interprets as local time
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };
  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      type: ev.type,
      title: ev.title,
      date: ev.date,
      start_time: ev.start_time,
      duration_minutes: ev.duration_minutes,
      num_courts: ev.num_courts,
      max_participants: ev.max_participants,
      price_per_player: ev.price_per_player || 0,
      description: ev.description || '',
      weekly_recurring: !!ev.weekly_recurring,
      weekly_end_date: ev.weekly_end_date || '',
      signup_opens_weekday: (ev.signup_opens_weekday !== undefined && ev.signup_opens_weekday !== null) ? String(ev.signup_opens_weekday) : '',
      signup_opens_time: ev.signup_opens_time || '',
      cancellation_cutoff_hours: ev.cancellation_cutoff_hours ?? 6,
      players_per_court: ev.players_per_court ?? 4,
      signup_opens_at: ev.signup_opens_at ? isoToLocalInput(ev.signup_opens_at) : '',
      members_only: !!ev.members_only,
      signup_mode: ev.signup_mode || 'solo',
      member_price: ev.member_price != null ? ev.member_price : '',
      pay_at_club_only: !!ev.pay_at_club_only,
      min_level: (ev.min_level ?? ev.min_level === 0) ? String(ev.min_level) : '',
      max_level: (ev.max_level ?? ev.max_level === 0) ? String(ev.max_level) : '',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.title || !form.date || !form.start_time) {
        toast.error(L('Please fill required fields', 'Bitte Pflichtfelder ausfüllen'));
        return;
      }
      const payload = {
        ...form,
        duration_minutes: parseInt(form.duration_minutes) || 120,
        num_courts: parseInt(form.num_courts) || 8,
        max_participants: parseInt(form.max_participants) || 32,
        price_per_player: parseFloat(form.price_per_player) || 0,
        cancellation_cutoff_hours: parseInt(form.cancellation_cutoff_hours) || 6,
        players_per_court: parseInt(form.players_per_court) || 4,
        min_level: form.min_level !== '' ? parseFloat(form.min_level) : null,
        max_level: form.max_level !== '' ? parseFloat(form.max_level) : null,
        weekly_end_date: form.weekly_recurring && form.weekly_end_date ? form.weekly_end_date : null,
        signup_opens_weekday: (form.signup_opens_weekday !== '' && form.signup_opens_weekday !== null && form.signup_opens_weekday !== undefined)
          ? parseInt(form.signup_opens_weekday)
          : null,
        signup_opens_time: form.signup_opens_time || null,
        // If a weekday+time rule is set, backend will compute signup_opens_at.
        // Otherwise fall back to the manual datetime override (or "now").
        signup_opens_at: (form.signup_opens_weekday !== '' && form.signup_opens_time)
          ? null
          : (form.signup_opens_at ? localInputToIso(form.signup_opens_at) : new Date().toISOString()),
        members_only: !!form.members_only,
        signup_mode: form.signup_mode || 'solo',
        member_price: form.member_price === '' || form.member_price == null ? null : parseFloat(form.member_price),
        pay_at_club_only: !!form.pay_at_club_only,
      };
      if (editingEvent) {
        await API.put(`/community/events/${editingEvent.id}`, payload);
        toast.success(L('Event updated', 'Event aktualisiert'));
      } else {
        await API.post('/community/events', payload);
        const countMsg = payload.weekly_recurring
          ? (payload.weekly_end_date
              ? L(`Event + weekly series until ${payload.weekly_end_date} created`, `Event + Wochen-Serie bis ${payload.weekly_end_date} erstellt`)
              : L('Event + 11 weekly editions created', 'Event + 11 Wochen-Editionen erstellt'))
          : L('Event created', 'Event erstellt');
        toast.success(countMsg);
      }
      setShowModal(false); setEditingEvent(null); setForm(emptyForm);
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const handleDelete = (ev) => {
    const isSeries = ev.weekly_recurring || !!ev.parent_event_id;
    setConfirmDelete({
      type: isSeries ? 'series-or-one' : 'event',
      ev,
      title: ev.title,
      date: ev.date,
    });
  };

  const performDeleteEvent = async (ev, cascade = false) => {
    try {
      const url = `/community/events/${ev.id}${cascade ? '?delete_series=true' : ''}`;
      await API.delete(url);
      toast.success(cascade ? L('Series deleted', 'Serie gelöscht') : L('Deleted', 'Gelöscht'));
      setConfirmDelete(null);
      // If we just deleted while in series-detail and series is now empty, go back
      if (cascade) setSelectedSeries(null);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  // Signups without a payment page get an explicit "are you sure?" step
  const requestSignup = (ev, opts, isFull) => {
    const mode = ev.signup_mode || 'solo';
    const price = ev.price_per_player || 0;
    const freeSpot = (ev.prepaid_open_slots || 0) > 0 && price > 0;
    if (mode === 'solo' && (isFull || opts.payAtClub || price <= 0 || freeSpot)) {
      setConfirmSignup({ ev, opts, kind: isFull ? 'waitlist' : (opts.payAtClub && !freeSpot) ? 'pay_at_club' : 'free' });
      return;
    }
    handleSignup(ev, opts);
  };

  const handleSignup = async (ev, opts = {}) => {
    // Team-signup gate: if the event requires or allows a partner and the caller
    // didn't already provide one via the dialog, open the partner picker.
    // IMPORTANT: forward `payAtClub` so a partner-mode "pay at club" click
    // still bypasses Datatrans after the partner has been chosen. Was
    // silently dropped before → both got redirected to card checkout.
    if (!opts.skipPartnerCheck) {
      const mode = ev.signup_mode || 'solo';
      if (mode === 'partner' || mode === 'both') {
        setPartnerSignup({ event: ev, mode, partner: null, payForBoth: false, payAtClub: !!opts.payAtClub });
        return;
      }
    }
    try {
      const body = { origin_url: window.location.origin };
      if (opts.partnerId) body.partner_id = opts.partnerId;
      if (opts.payForBoth) body.pay_for_both = true;
      if (opts.payAtClub) body.pay_at_club = true;
      const { data } = await API.post(`/community/events/${ev.id}/signup`, body);
      // Pay-First flow: backend may return a Datatrans hosted-payment URL
      if (data.checkout_url) {
        const holdMin = data.hold_minutes;
        const holdLabel = holdMin >= 60 ? `${Math.round(holdMin / 60)}h` : `${holdMin}m`;
        toast.info(L(`Redirecting to payment — you have ${holdLabel} to complete it`,
          `Weiterleitung zur Zahlung — du hast ${holdLabel} Zeit`));
        window.location.href = data.checkout_url;
        return;
      }
      if (data.pay_at_club) {
        toast.success(L("You're in — please pay at reception on arrival.",
          'Du bist dabei — bitte im Club an der Rezeption bezahlen.'));
      } else if (data.free_reclaim) {
        toast.success(L("You're in — free spot (already paid by a late-cancellation)!",
          'Du bist dabei — Gratisplatz (durch späte Abmeldung bereits bezahlt)!'));
      } else if (data.just_added_to_waitlist) {
        toast.info(L('Event is full — added to waitlist (free)', 'Event ist voll — auf Warteliste (kostenlos)'));
      } else if (data.partner_signup) {
        toast.success(L(`You and ${data.partner_signup.name} are signed up as a team!`, `Du und ${data.partner_signup.name} seid als Team angemeldet!`));
      } else {
        toast.success(L("You're signed up!", 'Du bist angemeldet!'));
      }
      setPartnerSignup(null);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  // Admin quick action — marks a `pay_at_club` participant as paid at the desk
  // and pushes the amount into the Kasse.
  const markPaidAtClub = async (ev, participant) => {
    if (!isAdminOrStaff) return;
    const methods = [
      { key: 'cash',   label: L('Cash', 'Bar') },
      { key: 'card',   label: L('Card', 'Karte') },
      { key: 'css_coins', label: 'CSS Coins' },
      { key: 'voucher', label: L('Voucher', 'Gutschein') },
      { key: 'invoice',label: L('Invoice', 'Rechnung') },
    ];
    setPayPick({
      title: L(`Mark ${participant.user_name} as paid`, `${participant.user_name} als bezahlt markieren`),
      subtitle: L('How was it paid?', 'Wie wurde bezahlt?'),
      methods,
      run: (chosen) => settlePaidAtClub(ev, participant, chosen),
    });
  };

  const settlePaidAtClub = async (ev, participant, chosen) => {
    if (chosen.key === 'voucher') {
      setPayPick(null);
      setSettleFor({ ev, p: participant, method: 'voucher' });
      return;
    }
    if (chosen.key === 'card') {
      const amt = participant.user_is_member && ev.member_price != null ? Number(ev.member_price) : Number(ev.price_per_player || 0);
      const term = await payByCard(amt, `Mixin ${participant.user_name}`);
      if (!term.ok) {
        toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}`, { duration: 10000 });
        return;
      }
    }
    try {
      const { data } = await API.post(
        `/admin/community/events/${ev.id}/participants/${participant.user_id}/mark-paid-at-club`,
        { payment_method: chosen.key },
      );
      toast.success(L(
        `Paid ${data.amount_settled?.toFixed(2) || ''} CHF via ${chosen.label} — logged to Kasse.`,
        `Bezahlt ${data.amount_settled?.toFixed(2) || ''} CHF (${chosen.label}) — in der Kasse verbucht.`,
      ));
      setPayPick(null);
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const handlePayNow = async (ev) => {
    try {
      const { data } = await API.post(`/community/events/${ev.id}/pay`, { origin_url: window.location.origin });
      if (data.checkout_url) {
        toast.info(L('Redirecting to payment…', 'Weiterleitung zur Zahlung…'));
        window.location.href = data.checkout_url;
      } else if (data.already_paid) {
        toast.success(L('Already paid', 'Bereits bezahlt'));
        loadEvents();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const handleConfirmPayAtClub = async (ev) => {
    try {
      await API.post(`/community/events/${ev.id}/confirm_pay_at_club`);
      toast.success(L('Spot confirmed — pay at the club', 'Platz bestätigt — Bezahlung im Club'));
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const performCancel = async (ev, confirmLateCancel = false) => {
    try {
      // axios.delete with body uses `data` key
      const { data } = await API.delete(`/community/events/${ev.id}/signup`, {
        data: { confirm_late_cancel: confirmLateCancel, confirm_late_fee: confirmLateCancel, origin_url: window.location.origin },
      });
      if (data.late_cancellation_no_refund) {
        toast.warning(L(
          'Cancelled — no refund (within cutoff). Your spot is now offered free to the next signup.',
          'Abgemeldet — keine Rückerstattung (innerhalb der Frist). Dein Platz wird dem nächsten gratis angeboten.'
        ));
      } else if (data.refund_issued) {
        toast.success(L(`Cancelled — ${data.refund_amount?.toFixed(2)} CHF refunded`,
          `Abgemeldet — ${data.refund_amount?.toFixed(2)} CHF erstattet`));
      } else {
        toast.success(L('Unregistered', 'Abgemeldet'));
      }
      if (data.partner_also_removed) {
        toast.info(L(`${data.partner_also_removed} was removed as well.`, `${data.partner_also_removed} wurde ebenfalls abgemeldet.`));
      }
      setLateFeeConfirm(null);
      loadEvents();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && (detail?.code === 'late_cancel_no_refund' || detail?.code === 'late_cancel_fee_required')) {
        // Show confirmation modal
        setLateFeeConfirm({
          event: ev,
          feeAmount: detail.fee_amount,
          hoursUntilStart: detail.hours_until_start,
          cutoffHours: detail.cutoff_hours,
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : L('Error', 'Fehler'));
      }
    }
  };

  const handleCancel = (ev) => {
    setConfirmDelete({ type: 'unregister', ev });
  };

  const performUnregister = async (ev) => {
    setConfirmDelete(null);
    await performCancel(ev, false);
  };

  const handleAdminAdd = async (eventId, userId) => {
    try {
      const { data } = await API.post(`/community/events/${eventId}/admin-add`, { user_id: userId });
      if (data?.just_added_to_waitlist) toast.info(L('Lineup already checked — player added to the waitlist', 'Aufstellung bereits geprüft — Spieler auf die Warteliste gesetzt'));
      else toast.success(L('Player added', 'Spieler hinzugefügt'));
      setShowAddPlayer(null); setSearchQuery(''); setSearchResults([]);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const handleAdminRemove = (eventId, userId, name) => {
    setConfirmDelete({ type: 'player', eventId, userId, userName: name });
  };

  const handleWaitlistPromote = async (eventId, userId, name) => {
    if (!window.confirm(L(`Move ${name} up from the waitlist?`, `${name} von der Warteliste nachrücken lassen?`))) return;
    try {
      await API.post(`/community/events/${eventId}/waitlist/${userId}/promote`);
      toast.success(L(`${name} moved up`, `${name} ist nachgerückt`));
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const performAdminRemove = async (eventId, userId) => {
    try {
      await API.delete(`/community/events/${eventId}/admin-remove/${userId}`);
      toast.success(L('Removed', 'Entfernt'));
      setConfirmDelete(null);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const formatEventDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return format(d, 'EEEE, dd. MMM yyyy', { locale: dateLocale });
    } catch { return dateStr; }
  };

  const formatSignupOpensAt = (iso) => {
    try {
      const d = new Date(iso);
      return format(d, 'EEE dd.MM., HH:mm', { locale: dateLocale });
    } catch { return iso; }
  };

  // Live countdown until signup opens, e.g. "in 02:14" / "in 1d 03h".
  // Returns null if the timestamp is already in the past.
  const formatCountdown = (iso) => {
    try {
      const target = new Date(iso).getTime();
      const diff = Math.max(0, target - now);
      if (diff <= 0) return null;
      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;
      if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h`;
      if (hours > 0) return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    } catch { return null; }
  };

  // Chunk participants into courts of N (default 4)
  const chunkByCourt = (parts, perCourt = 4) => {
    const chunks = [];
    for (let i = 0; i < parts.length; i += perCourt) chunks.push(parts.slice(i, i + perCourt));
    return chunks;
  };

  // Map player level to club-color: 1.0–1.9 RED (best, top court), 2.0–3.9 orange, 4.0–5.9 yellow, 6.0–7.9 GREEN (beginners, bottom court)
  const levelColor = (lvl) => {
    if (lvl == null) return { bg: 'bg-slate-300', text: 'text-white', ring: 'ring-slate-200', label: '?', name: 'unknown' };
    const v = Number(lvl);
    if (v < 2.0) return { bg: 'bg-red-500', text: 'text-white', ring: 'ring-red-200', label: v.toFixed(2), name: 'red' };
    if (v < 4.0) return { bg: 'bg-orange-500', text: 'text-white', ring: 'ring-orange-200', label: v.toFixed(2), name: 'orange' };
    if (v < 6.0) return { bg: 'bg-yellow-400', text: 'text-yellow-950', ring: 'ring-yellow-200', label: v.toFixed(2), name: 'yellow' };
    return { bg: 'bg-green-500', text: 'text-white', ring: 'ring-green-200', label: v.toFixed(2), name: 'green' };
  };

  // Add minutes to "HH:MM" -> "HH:MM"
  const addMinutes = (hhmm, minutes) => {
    try {
      const [h, m] = (hhmm || '00:00').split(':').map(Number);
      const total = (h * 60 + m + (minutes || 0)) % (24 * 60);
      return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    } catch { return hhmm; }
  };

  // Group events into "series": one card per recurring weekly series (or singleton).
  // Grouping key precedence:
  //   1. parent_event_id (auto-recurring series stay tightly linked)
  //   2. own id as parent (recurring parent event)
  //   3. title + weekday + start_time (manual siblings — "Sunday Mixin @ 20:00"
  //      created ad-hoc on different Sundays still land in one series card)
  const seriesGroups = useMemo(() => {
    const groups = new Map();
    const weekdayOf = (dateStr) => {
      try { return new Date(dateStr + 'T00:00:00').getDay(); } catch { return -1; }
    };
    // First pass: canonical group id per event (prefer parent_event_id)
    const parentOwners = new Set();
    for (const ev of events) {
      if (ev.weekly_recurring && !ev.parent_event_id) parentOwners.add(ev.id);
    }
    for (const ev of events) {
      let key;
      if (ev.parent_event_id) {
        key = ev.parent_event_id;
      } else if (parentOwners.has(ev.id)) {
        key = ev.id;
      } else {
        // Ad-hoc grouping: same title + weekday + start_time counts as one series.
        const t = (ev.title || '').trim().toLowerCase();
        key = `manual::${t}::${weekdayOf(ev.date)}::${ev.start_time}`;
      }
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(ev);
    }
    // Convert to array, sort each group by date+time, then sort groups by next-upcoming
    const out = [];
    for (const [key, list] of groups.entries()) {
      list.sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
      const first = list[0];
      const totalConfirmed = list.reduce((s, e) => s + (e.confirmed_count || 0), 0);
      const myUpcoming = list.filter(e => e.i_am_signed_up).length;
      const myWaitlist = list.filter(e => e.i_am_on_waitlist).length;
      out.push({
        key,
        events: list,
        title: first.title,
        type: first.type,
        // Explicit weekly (opted in by admin OR linked via parent_event_id)
        weekly: !!first.weekly_recurring || list.some(e => !!e.parent_event_id),
        // Softer badge: manually-created siblings simply have multiple editions
        // but weren't declared "weekly_recurring" — no false-positive scheduling claim.
        hasMultipleEditions: list.length > 1,
        start_time: first.start_time,
        end_time: addMinutes(first.start_time, first.duration_minutes),
        duration_minutes: first.duration_minutes,
        weekday: first.date,
        nextDate: first.date,
        editionCount: list.length,
        totalCapacity: first.max_participants,
        pricePerPlayer: first.price_per_player,
        totalConfirmed,
        myUpcoming,
        myWaitlist,
        firstEvent: first,
      });
    }
    out.sort((a, b) => (a.nextDate + a.start_time).localeCompare(b.nextDate + b.start_time));
    return out;
  }, [events]);

  const formatWeekday = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return format(d, 'EEEE', { locale: dateLocale });
    } catch { return ''; }
  };
  const formatShortDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return format(d, 'dd. MMM', { locale: dateLocale });
    } catch { return dateStr; }
  };

  const selectedGroup = selectedSeries ? seriesGroups.find(g => g.key === selectedSeries) : null;
  const visibleEvents = selectedGroup ? selectedGroup.events : [];

  // If selected series no longer exists (e.g. deleted), drop back to series overview
  useEffect(() => {
    if (selectedSeries && !loading && !selectedGroup) {
      setSelectedSeries(null);
    }
  }, [selectedSeries, selectedGroup, loading]);

  // Re-open the participant list we came from (back-button from a customer tab).
  const [restoreTarget] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return { event: q.get('open_event'), pending: q.get('open_pending') };
  });
  useEffect(() => {
    const { event: evId, pending: peId } = restoreTarget;
    if ((!evId && !peId) || restoreTarget.done || seriesGroups.length === 0) return;
    restoreTarget.done = true;
    if (evId) {
      const grp = seriesGroups.find(g => g.events.some(e => e.id === evId));
      if (grp) setSelectedSeries(grp.key);
      setExpanded(prev => ({ ...prev, [evId]: true }));
    }
    if (peId) setExpandedPending(prev => ({ ...prev, [peId]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${evId ? `event-${evId}` : `pending-close-${peId}`}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
  }, [restoreTarget, seriesGroups]);

  return (
    <div className="space-y-6 pb-12">
      <MixinSettleDialog target={settleFor} API={API} L={L} language={language}
        onDone={() => { setSettleFor(null); loadEvents(); }} onClose={() => setSettleFor(null)} />
      <SignupConfirmDialog
        request={confirmSignup}
        L={L}
        price={confirmSignup ? formatPrice(priceFor(confirmSignup.ev, user?.is_member)) : ''}
        dateLabel={confirmSignup ? formatEventDate(confirmSignup.ev.date) : ''}
        onClose={() => setConfirmSignup(null)}
        onConfirm={() => { const r = confirmSignup; setConfirmSignup(null); handleSignup(r.ev, r.opts); }}
      />
      <PaymentMethodPicker request={payPick} onPick={(m) => payPick?.run(m)} onClose={() => setPayPick(null)} cancelLabel={L('Cancel', 'Abbrechen')} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <Confetti size={32} weight="duotone" className="text-purple-500" />
            {L('Community Padelarena', 'Community Padelarena')}
          </h1>
        </div>
        {isAdminOrStaff && (
          <Button onClick={openCreate} className="bg-purple-500 text-white hover:bg-purple-600 font-bold" data-testid="create-event-btn">
            <Plus size={16} className="mr-1" /> {L('New Event', 'Neues Event')}
          </Button>
        )}
      </div>


      {loading && !initialLoaded ? (
        <p className="text-slate-400 text-sm italic">{L('Loading…', 'Lädt…')}</p>
      ) : events.length === 0 ? (
        <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-12 text-center">
          <Confetti size={48} weight="thin" className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400 text-sm italic">
            {L('No upcoming events.', 'Keine bevorstehenden Events.')}
            {isAdminOrStaff && <> {L('Click "New Event" to create one.', 'Klicke "Neues Event" zum Anlegen.')}</>}
          </p>
        </div>
      ) : !selectedSeries ? (        // ----- Series overview (one card per recurring series) -----
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {seriesGroups.map(g => {
            const weekdayLabel = formatWeekday(g.nextDate);
            const isSeries = g.weekly && g.editionCount > 1;
            const isManualMulti = !g.weekly && g.hasMultipleEditions;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => setSelectedSeries(g.key)}
                className="text-left bg-white border border-slate-200/60 rounded-2xl shadow-sm p-5 hover:shadow-md hover:border-purple-300 hover:-translate-y-0.5 transition-all"
                data-testid={`series-${g.key}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                      g.type === 'mixin' ? 'bg-purple-100 text-purple-700' :
                      g.type === 'tournament' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{g.type}</span>
                    {isSeries && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700">
                        {L('weekly', 'wöchentlich')}
                      </span>
                    )}
                    {isManualMulti && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                        {L('multiple dates', 'mehrere Termine')}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {g.editionCount}× {L('upcoming', 'Termine')}
                  </span>
                </div>
                <h2 className="text-lg font-bold text-slate-900 leading-tight">{g.title}</h2>
                <p className="text-sm text-slate-500 mt-0.5 capitalize">{weekdayLabel}</p>
                <div className="mt-3 flex items-center gap-3 text-sm text-slate-600 flex-wrap">
                  <span className="flex items-center gap-1"><Clock size={14} /> {g.start_time}–{g.end_time}</span>
                  <span className="text-slate-300">•</span>
                  <span>{L('next', 'nächster')}: <strong className="text-slate-800">{formatShortDate(g.nextDate)}</strong></span>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  {(g.pricePerPlayer || 0) > 0 ? (
                    <span className="text-emerald-700 font-bold">{g.pricePerPlayer.toFixed(0)} CHF</span>
                  ) : <span className="text-slate-300">—</span>}
                  {g.myUpcoming > 0 ? (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700" data-testid={`my-upcoming-${g.key}`}>
                      <CheckCircle size={11} className="inline mr-0.5" /> {L(`in ${g.myUpcoming}`, `${g.myUpcoming} angemeldet`)}
                    </span>
                  ) : g.myWaitlist > 0 ? (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-amber-100 text-amber-700">
                      {L(`waitlist ${g.myWaitlist}`, `Warteliste ${g.myWaitlist}`)}
                    </span>
                  ) : (
                    <span className="text-slate-400">{L('Tap to view', 'Antippen zum Anzeigen')} →</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        // ----- Drill-down: all editions of one selected series -----
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setSelectedSeries(null)}
              className="text-sm font-semibold text-purple-600 hover:text-purple-800 transition-colors"
              data-testid="back-to-series">
              ← {L('All series', 'Alle Serien')}
            </button>
            <div className="text-right">
              <p className="text-xs text-slate-400 uppercase tracking-wider">{formatWeekday(selectedGroup.nextDate)} {selectedGroup.start_time}</p>
              <h2 className="text-xl font-bold text-slate-900">{selectedGroup.title}</h2>
            </div>
          </div>

        <div className="space-y-4">
          {visibleEvents.map(ev => {
            const isOpen = expanded[ev.id];
            const filledPct = ev.max_participants ? (ev.confirmed_count / ev.max_participants) * 100 : 0;
            const cancelled = ev.status === 'cancelled';
            const completed = ev.status === 'completed';
            const isFull = ev.confirmed_count >= ev.max_participants;
            const signupClosed = ev.signup_is_open === false;
            const perCourt = ev.players_per_court || 4;
            const cutoff = ev.cancellation_cutoff_hours ?? 6;
            const insideCutoff = ev.hours_until_start != null && ev.hours_until_start < cutoff && ev.hours_until_start > 0;
            const freeReclaimAvailable = (ev.prepaid_open_slots || 0) > 0 && (ev.price_per_player || 0) > 0;
            // My participant entry (if any) — used to detect awaiting_payment state
            const myEntry = (ev.participants || []).find(p => p.user_id === user?.id);
            const myAwaitingPayment = myEntry && !myEntry.waitlist && myEntry.payment_status === 'awaiting_payment';
            const myWasPromoted = myEntry && myEntry.promoted_at;
            // Participants display order:
            //  1) Signup phase (before admin confirms lineup): FIFO by signup
            //     time — first-come-first-serve, list stays up to date live.
            //  2) After admin opens the Lineup board & confirms it
            //     (`lineup_locked`): group into courts using each player's
            //     persisted `start_court` (which the admin fine-tuned via
            //     drag & drop). Fallback: level-sort. Waitlist always last.
            const isLineupLocked = !!ev.lineup_locked;
            const sortedParts = [...(ev.participants || [])].sort((a, b) => {
              if (a.waitlist !== b.waitlist) return a.waitlist ? 1 : -1;
              if (a.waitlist) return (a.signed_up_at || '').localeCompare(b.signed_up_at || '');
              if (isLineupLocked) {
                // Court-based ordering; unassigned goes to the end.
                const ca = a.start_court == null ? 99 : Number(a.start_court);
                const cb = b.start_court == null ? 99 : Number(b.start_court);
                if (ca !== cb) return ca - cb;
                // Within a court: honour the admin's manually-saved
                // `court_position` (from the Aufstellung drag & drop).
                // Only fall back to level-sort when a court has NO manual
                // positions saved (legacy events pre-lineup-board).
                const pa = a.court_position == null ? null : Number(a.court_position);
                const pb = b.court_position == null ? null : Number(b.court_position);
                if (pa != null && pb != null) return pa - pb;
                if (pa != null) return -1;
                if (pb != null) return 1;
                const la = a.user_level == null ? 99 : Number(a.user_level);
                const lb = b.user_level == null ? 99 : Number(b.user_level);
                if (la !== lb) return la - lb;
                return (a.signed_up_at || '').localeCompare(b.signed_up_at || '');
              }
              // Signup phase — pure FIFO.
              return (a.signed_up_at || '').localeCompare(b.signed_up_at || '');
            });
            const confirmedParts = sortedParts.filter(p => !p.waitlist);
            const waitlistParts = sortedParts.filter(p => p.waitlist);
            const courts = chunkByCourt(confirmedParts, perCourt);

            // Team-signup grouping — anyone with a shared `signup_group_id`
            // signed up together (e.g. Sunday Early-Bird couple mode). Assign
            // each group a stable letter (A/B/C…) in the order they appear
            // so the badges match across the FIFO list, the court view and
            // the summary bar.
            const teamLetterByGroup = {};
            let teamLetterIdx = 0;
            for (const p of confirmedParts) {
              const gid = p.signup_group_id;
              if (!gid || teamLetterByGroup[gid] !== undefined) continue;
              teamLetterByGroup[gid] = String.fromCharCode(65 + (teamLetterIdx % 26));
              teamLetterIdx += 1;
            }
            const teamedCount = confirmedParts.filter(p => p.signup_group_id).length;
            const soloCount = confirmedParts.length - teamedCount;
            const teamCount = Object.keys(teamLetterByGroup).length;
            const teamColorClasses = [
              'bg-pink-100 text-pink-700',
              'bg-indigo-100 text-indigo-700',
              'bg-orange-100 text-orange-700',
              'bg-teal-100 text-teal-700',
              'bg-fuchsia-100 text-fuchsia-700',
              'bg-lime-100 text-lime-700',
              'bg-cyan-100 text-cyan-700',
              'bg-rose-100 text-rose-700',
            ];
            const teamBadge = (p) => {
              const letter = p.signup_group_id ? teamLetterByGroup[p.signup_group_id] : null;
              if (!letter) return null;
              const idx = letter.charCodeAt(0) - 65;
              const cls = teamColorClasses[idx % teamColorClasses.length];
              return (
                <span
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wide ${cls}`}
                  title={L(`Signed up as a team (Team ${letter})`, `Als Team angemeldet (Team ${letter})`)}
                  data-testid={`team-badge-${p.user_id}`}
                >
                  🤝 {letter}
                </span>
              );
            };

            return (
              <div key={ev.id} className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-all ${cancelled || completed ? 'opacity-60' : 'border-slate-200/60'}`} data-testid={`event-${ev.id}`}>
                <div className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                          ev.type === 'mixin' ? 'bg-purple-100 text-purple-700' :
                          ev.type === 'tournament' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {ev.type}
                        </span>
                        {ev.weekly_recurring && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700" data-testid={`weekly-badge-${ev.id}`}>
                            {L('weekly', 'wöchentlich')}
                          </span>
                        )}
                        {cancelled && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-red-100 text-red-700">{L('cancelled', 'abgesagt')}</span>}
                        {completed && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{L('completed', 'beendet')}</span>}
                        {ev.members_only && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-sky-100 text-sky-700 flex items-center gap-1" data-testid={`members-only-${ev.id}`}>
                            <SealCheck size={10} weight="fill" /> {L('Members only', 'Mitglieder')}
                          </span>
                        )}
                        {/* Level-band badge — colour mirrors the Mixin-level
                            palette (6+ green, 4-5.9 yellow, 2-3.9 orange,
                            <2 red). Shows the gated range to users BEFORE
                            they try to sign up and get rejected. */}
                        {(ev.min_level != null || ev.max_level != null) && (() => {
                          const lo = ev.min_level;
                          const hi = ev.max_level;
                          // Pick colour from the min-level if present, else the max.
                          const refLvl = lo != null ? lo : hi;
                          let cls = 'bg-slate-100 text-slate-700';
                          if (refLvl != null) {
                            if (refLvl < 2) cls = 'bg-red-100 text-red-700';
                            else if (refLvl < 4) cls = 'bg-orange-100 text-orange-700';
                            else if (refLvl < 6) cls = 'bg-yellow-100 text-yellow-800';
                            else cls = 'bg-green-100 text-green-700';
                          }
                          const label =
                            lo != null && hi != null ? `${Number(lo).toFixed(2)}–${Number(hi).toFixed(2)}` :
                            lo != null ? `≥ ${Number(lo).toFixed(2)}` :
                            `≤ ${Number(hi).toFixed(2)}`;
                          return (
                            <span
                              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md flex items-center gap-1 ${cls}`}
                              title={L(`Only level ${label} players may sign up`, `Nur Level ${label} dürfen sich anmelden`)}
                              data-testid={`level-gate-${ev.id}`}
                            >
                              🎾 Level {label}
                            </span>
                          );
                        })()}
                        {ev.i_am_block_pending && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-amber-100 text-amber-800" data-testid={`my-block-pending-${ev.id}`}>
                            {L(`Waiting for court to fill (${ev.block_missing} missing)`, `Wartet auf Platzvervollständigung (${ev.block_missing} ${ev.block_missing === 1 ? 'Platz fehlt' : 'Plätze fehlen'})`)}
                          </span>
                        )}
                        {ev.i_am_signed_up && !myAwaitingPayment && !ev.i_am_block_pending && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700"><CheckCircle size={11} className="inline mr-0.5" /> {L('You are in', 'Du bist dabei')}</span>}
                        {ev.lineup_locked && ev.my_court && (
                          <span className="text-[11px] font-extrabold uppercase px-2 py-0.5 rounded-md bg-purple-600 text-white shadow-sm" data-testid={`my-court-badge-${ev.id}`}>
                            🎾 Court {ev.my_court}
                          </span>
                        )}
                        {myAwaitingPayment && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-amber-100 text-amber-700" data-testid={`my-pending-payment-${ev.id}`}>{L('payment due', 'Zahlung offen')}</span>}
                        {ev.i_am_on_waitlist && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-amber-100 text-amber-700">{L('on waitlist', 'Warteliste')}</span>}
                        {freeReclaimAvailable && !ev.i_am_signed_up && !ev.i_am_on_waitlist && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-fuchsia-100 text-fuchsia-700" data-testid={`free-spot-${ev.id}`}>
                            🎁 {L(`${ev.prepaid_open_slots} free spot${ev.prepaid_open_slots > 1 ? 's' : ''}`, `${ev.prepaid_open_slots} Gratisplatz${ev.prepaid_open_slots > 1 ? '/Plätze' : ''}`)}
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-bold text-slate-900 leading-tight">{ev.title}</h2>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                        <span className="flex items-center gap-1"><CalendarBlank size={14} /> {formatEventDate(ev.date)}</span>
                        <span className="flex items-center gap-1"><Clock size={14} /> {ev.start_time} ({Math.round(ev.duration_minutes/60*10)/10}h)</span>
                        <span className="flex items-center gap-1"><MapPin size={14} /> {ev.num_courts} Courts</span>
                        {(ev.price_per_player || 0) > 0 && <span className="text-emerald-700 font-semibold" data-testid={`event-price-${ev.id}`}>{ev.price_per_player.toFixed(2)} CHF{ev.member_price != null && <span className="text-sky-700"> · Members {Number(ev.member_price).toFixed(2)} CHF</span>}</span>}
                      </div>
                      {ev.description && <p className="text-sm text-slate-500 mt-2 leading-relaxed">{ev.description}</p>}
                      {(ev.pay_at_club_only || ev.signup_mode === 'partner') && (
                        <p className="text-[11px] text-emerald-700 mt-1.5 leading-relaxed font-medium" data-testid={`event-rules-${ev.id}`}>
                          {ev.pay_at_club_only && L('Payment at the club only (cash / card at reception). ', 'Bezahlung nur im Club (bar / Karte am Empfang). ')}
                          {ev.signup_mode === 'partner' && L('Signup as a team only — if one partner cancels, both are removed.', 'Anmeldung nur zu zweit — meldet sich eine Person ab, werden beide abgemeldet.')}
                        </p>
                      )}
                      {(ev.price_per_player || 0) > 0 && !ev.pay_at_club_only && (
                        <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed" data-testid={`policy-summary-${ev.id}`}>
                          {L(
                            `📌 Signup only becomes valid after payment (online or by choosing 'pay at the club'). Spot is held for 24h to complete payment (1h if same-day, 8h if promoted from waitlist). Free refund if you cancel >${cutoff}h before start. Late cancel = no refund, but the spot becomes free for the next signup.`,
                            `📌 Anmeldung gilt erst nach Zahlung (online oder mit Wahl «im Club zahlen»). Platz wird 24h reserviert (1h taggleich, 8h bei Aufrücken von der Warteliste). Kostenfrei stornierbar bis ${cutoff}h vor Start. Späte Abmeldung = keine Rückerstattung, aber der Platz wird dem nächsten gratis angeboten.`
                          )}
                        </p>
                      )}
                      {signupClosed && (ev.my_signup_opens_at || ev.signup_opens_at) && (() => {
                        const countdown = formatCountdown((ev.my_signup_opens_at || ev.signup_opens_at));
                        // Pulse the last 60 s so people know it's about to unlock.
                        const isImminent = countdown && !countdown.includes('h') && !countdown.includes('d') &&
                                            Number(countdown.split(':')[0]) === 0;
                        return (
                          <div
                            className={`mt-2 flex items-center gap-1.5 text-xs px-2 py-1 rounded-md w-fit border ${
                              isImminent
                                ? 'text-emerald-800 bg-emerald-50 border-emerald-300 animate-pulse font-bold'
                                : 'text-amber-700 bg-amber-50 border-amber-200'
                            }`}
                            data-testid={`signup-opens-at-${ev.id}`}
                          >
                            <Lock size={12} weight="fill" />
                            {countdown ? (
                              <>
                                {L('Signup opens in', 'Anmeldung öffnet in')}{' '}
                                <span className="font-mono font-bold tabular-nums">{countdown}</span>
                              </>
                            ) : (
                              <>{L('Signup opens', 'Anmeldung öffnet')} {formatSignupOpensAt((ev.my_signup_opens_at || ev.signup_opens_at))}</>
                            )}
                          </div>
                        );
                      })()}
                      {insideCutoff && ev.i_am_signed_up && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md w-fit" data-testid={`cutoff-warning-${ev.id}`}>
                          <Warning size={12} weight="fill" />
                          {L(`Cancel now = no refund (within ${cutoff}h cutoff)`,
                              `Abmeldung jetzt = keine Rückerstattung (innerhalb ${cutoff}h Frist)`)}
                        </div>
                      )}
                      {myAwaitingPayment && (() => {
                        // Distinguish "just started paying, waiting for
                        // confirmation" from "hasn't paid yet at all". The
                        // former shows a calm blue banner; the latter shows
                        // the urgent amber one.
                        const signedUpMs = myEntry?.signed_up_at ? Date.parse(myEntry.signed_up_at) : 0;
                        const ageMin = signedUpMs ? (Date.now() - signedUpMs) / 60000 : 999;
                        const looksLikeReconciling = ageMin < 5 && !myWasPromoted;   // paid within last 5 min? probably reconciling
                        return (
                        <div className={`mt-3 rounded-lg border-2 p-3.5 shadow-sm ${looksLikeReconciling ? 'border-sky-300 bg-gradient-to-r from-sky-50 to-blue-50' : 'border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50'}`} data-testid={`payment-pending-${ev.id}`}>
                          <div className="flex items-start gap-2.5">
                            <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full text-white flex items-center justify-center ${looksLikeReconciling ? 'bg-sky-400' : 'bg-amber-400'}`}>
                              {looksLikeReconciling
                                ? <ArrowsClockwise size={18} className="animate-spin" />
                                : <Warning size={18} weight="fill" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-extrabold text-sm leading-tight ${looksLikeReconciling ? 'text-sky-900' : 'text-amber-900'}`}>
                                {looksLikeReconciling
                                  ? L('Payment being verified — hang tight…', 'Zahlung wird geprüft — einen Moment…')
                                  : L('Your signup is NOT yet valid — please confirm your spot.', 'Deine Anmeldung ist noch NICHT gültig — bitte bestätige deinen Platz.')}
                              </p>
                              <p className={`text-[12px] mt-1 leading-snug ${looksLikeReconciling ? 'text-sky-800' : 'text-amber-800'}`}>
                                {looksLikeReconciling
                                  ? L('If you just paid via Apple Pay/Card, this usually confirms within a few seconds. This page refreshes automatically.',
                                      'Falls du gerade via Apple Pay/Karte bezahlt hast, wird das meist in wenigen Sekunden bestätigt. Diese Seite aktualisiert sich automatisch.')
                                  : (myWasPromoted
                                      ? L('You moved up from the waitlist. Confirm your spot now — pay online or at the club — otherwise it will be released.',
                                          'Du bist von der Warteliste aufgerückt. Bestätige jetzt deinen Platz — online zahlen oder im Club — sonst wird er automatisch freigegeben.')
                                      : L('Complete payment to lock in your spot. Unpaid signups are auto-cancelled once the hold below expires.',
                                          'Zahle jetzt, um deinen Platz zu sichern. Nicht bezahlte Anmeldungen werden nach Ablauf der unten stehenden Frist automatisch storniert.'))}
                              </p>
                              {myEntry?.payment_expires_at && !looksLikeReconciling && (
                                <PaymentCountdown expiresAt={myEntry.payment_expires_at} language={language} testid={`countdown-${ev.id}`} />
                              )}
                              <div className="mt-2.5 flex flex-wrap gap-2">
                                <Button onClick={() => handlePayNow(ev)} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-sm" data-testid={`resume-pay-${ev.id}`}>
                                  <CheckCircle size={14} className="mr-1" /> {L(`Complete payment · ${formatPrice(priceFor(ev, user?.is_member))} CHF`, `Zahlung abschließen · ${formatPrice(priceFor(ev, user?.is_member))} CHF`)}
                                </Button>
                                <Button onClick={() => handleConfirmPayAtClub(ev)} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold" data-testid={`confirm-pay-at-club-${ev.id}`}>
                                  <CheckCircle size={14} className="mr-1" /> {L('Confirm · pay at the club', 'Bestätigen · im Club zahlen')}
                                </Button>
                                <Button onClick={() => handleCancel(ev)} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`cancel-pending-${ev.id}`}>
                                  <XCircle size={14} className="mr-1" /> {L('Cancel signup', 'Anmeldung abbrechen')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                    {isAdminOrStaff && !completed && (
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(ev)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" data-testid={`edit-event-${ev.id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => handleDelete(ev)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`delete-event-${ev.id}`}><Trash size={16} /></button>
                      </div>
                    )}
                  </div>

                  {/* Capacity */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-500 flex items-center gap-1.5"><Users size={14} /> {L('Players', 'Spieler')}</span>
                      <span className="font-bold text-slate-800">
                        {ev.confirmed_count} / {ev.max_participants}
                        {ev.block_pending_count > 0 && <span className="text-amber-600 font-semibold ml-2" data-testid={`block-pending-count-${ev.id}`}>({ev.confirmed_count - ev.block_pending_count} {L('confirmed', 'bestätigt')} + {ev.block_pending_count} {L('waiting', 'wartend')})</span>}
                        {ev.waitlist_count > 0 && <span className="text-amber-600 font-semibold ml-2">+ {ev.waitlist_count} {L('waitlist', 'Warteliste')}</span>}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${isFull ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, filledPct)}%` }} />
                    </div>
                  </div>

                  {/* Actions */}
                  {!cancelled && !completed && (
                    <div className="flex flex-wrap gap-2">
                      {ev.i_am_signed_up || ev.i_am_on_waitlist ? (
                        <>
                          {myAwaitingPayment && (
                            <Button onClick={() => handlePayNow(ev)} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold" data-testid={`pay-now-${ev.id}`}>
                              <CheckCircle size={16} className="mr-1" /> {L(`Pay ${formatPrice(priceFor(ev, user?.is_member))} CHF`, `${formatPrice(priceFor(ev, user?.is_member))} CHF zahlen`)}
                            </Button>
                          )}
                          {myAwaitingPayment && (
                            <Button onClick={() => handleConfirmPayAtClub(ev)} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold" data-testid={`confirm-pay-at-club-action-${ev.id}`}>
                              {L('Pay at the club', 'Im Club zahlen')}
                            </Button>
                          )}
                          <Button onClick={() => handleCancel(ev)} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`unregister-${ev.id}`}>
                            <XCircle size={16} className="mr-1" /> {L('Unregister', 'Abmelden')}
                          </Button>
                        </>
                      ) : (
                        (() => {
                          if (ev.results_finalized) return null;
                          const blockedByMembership = !!ev.members_only && !user?.is_member;
                          const disabled = signupClosed || blockedByMembership;
                          const priceGtZero = (ev.price_per_player || 0) > 0;
                          const clubOnly = priceGtZero && !!ev.pay_at_club_only;
                          const primaryLabel = blockedByMembership ? L('Members only', 'Nur Mitglieder')
                            : signupClosed ? L('Signup closed', 'Noch nicht offen')
                            : isFull ? L('Join waitlist', 'Auf Warteliste')
                            : clubOnly ? L('Sign up · Pay at club', 'Anmelden · Im Club zahlen')
                            : priceGtZero ? L('Sign me up · Pay online', 'Anmelden · Online zahlen')
                            : L('Sign me up', 'Anmelden');
                          return (
                            <>
                              <Button
                                onClick={() => requestSignup(ev, clubOnly ? { payAtClub: true } : {}, isFull)}
                                size="sm"
                                disabled={disabled}
                                className={`font-bold text-white ${disabled ? 'bg-slate-300 cursor-not-allowed' : isFull ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                                data-testid={`signup-${ev.id}`}
                                title={blockedByMembership ? L('Members only — contact the club to upgrade your account', 'Nur für Mitglieder — kontaktiere den Club, um Mitglied zu werden') : undefined}
                              >
                                {disabled ? <Lock size={16} className="mr-1" /> : <CheckCircle size={16} className="mr-1" />}
                                {primaryLabel}
                              </Button>
                              {!disabled && !isFull && priceGtZero && !clubOnly && (
                                <Button
                                  onClick={() => requestSignup(ev, { payAtClub: true }, false)}
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold"
                                  data-testid={`signup-pay-at-club-${ev.id}`}
                                  title={L('Reserve now, pay cash / card / voucher at reception on arrival',
                                           'Jetzt reservieren, im Club bar / Karte / Gutschein bezahlen')}
                                >
                                  <MapPin size={14} className="mr-1" /> {L('Sign up · Pay at club', 'Anmelden · Im Club zahlen')}
                                </Button>
                              )}
                            </>
                          );
                        })()
                      )}
                      <Button onClick={() => setExpanded(p => ({ ...p, [ev.id]: !p[ev.id] }))} variant="outline" size="sm" className="border-slate-200" data-testid={`toggle-list-${ev.id}`}>
                        {isOpen ? L('Hide list', 'Liste ausblenden') : L('Show list', 'Liste anzeigen')} ({ev.confirmed_count + ev.waitlist_count})
                      </Button>
                      {isAdminOrStaff && (
                        <Button onClick={() => setShowAddPlayer(ev.id)} variant="outline" size="sm" className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid={`admin-add-${ev.id}`}>
                          <Plus size={16} className="mr-1" /> {L('Add player', 'Spieler hinzufügen')}
                        </Button>
                      )}
                      {isAdminOrStaff && (ev.type === 'mixin' || ev.type === 'tournament') && ev.confirmed_count > 0 && (
                        <Button onClick={() => setLineupModal({ event: ev, mode: 'lineup' })} variant="outline" size="sm"
                          className={`${ev.lineup_locked ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
                          data-testid={`lineup-btn-${ev.id}`}>
                          {ev.lineup_locked ? <Lock size={14} className="mr-1" weight="fill" /> : <ListBullets size={14} className="mr-1" />}
                          {ev.lineup_locked ? L('Lineup ✓', 'Aufstellung ✓') : L('Lineup', 'Aufstellung')}
                        </Button>
                      )}
                      {isAdminOrStaff && (ev.type === 'mixin' || ev.type === 'tournament') && ev.lineup_locked && !ev.results_finalized && (
                        <Button onClick={() => setLineupModal({ event: ev, mode: 'results' })} variant="outline" size="sm"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`results-btn-${ev.id}`}>
                          <Trophy size={14} className="mr-1" /> {L('Enter results', 'Ergebnisse')}
                        </Button>
                      )}
                      {isAdminOrStaff && ev.results_finalized && (() => {
                        const ready = pendingCloseEvents.find(p => p.id === ev.id)?.ready_to_close;
                        return (
                          <Button onClick={() => closeEvent(ev)} size="sm"
                            className={`font-bold text-white ${ready ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-500 hover:bg-slate-600'}`}
                            data-testid={`close-mixin-${ev.id}`}>
                            <CheckCircle size={14} className="mr-1" />
                            {L('Close mixin', 'Mixin abschliessen')}{!ready && ` · ${L('open payments', 'offene Zahlungen')}`}
                          </Button>
                        );
                      })()}
                    </div>
                  )}

                  {/* Participants — FIFO list during signup, court groups after lineup confirm */}
                  {isOpen && (sortedParts.length > 0) && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h4 className="text-xs uppercase font-bold text-slate-400 tracking-wider">
                          {isLineupLocked
                            ? L('Participants — Court assignments (locked)', 'Teilnehmer — Court-Aufstellung (bestätigt)')
                            : L('Participants — order of signup (first come, first served)', 'Teilnehmer — nach Anmelde-Reihenfolge (first come, first served)')}
                        </h4>
                        {teamCount > 0 && (
                          <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
                            data-testid={`team-summary-${ev.id}`}
                            title={L('Team = 2 players who signed up together', 'Team = 2 Spieler, die zusammen angemeldet sind')}
                          >
                            🤝 {teamCount} {teamCount === 1 ? L('Team', 'Team') : L('Teams', 'Teams')} · {soloCount} {L('Solo', 'Solo')}
                          </span>
                        )}
                        {isLineupLocked && (
                          <div className="flex items-center gap-2 flex-wrap text-[10px]">
                            {[
                              { c: 'bg-red-500',     l: '1.0–1.9' },
                              { c: 'bg-orange-500',  l: '2.0–3.9' },
                              { c: 'bg-yellow-400',  l: '4.0–5.9' },
                              { c: 'bg-emerald-500', l: '6.0–7.9' },
                            ].map(({ c, l }) => (
                              <span key={l} className="inline-flex items-center gap-1 text-slate-500">
                                <span className={`inline-block w-2.5 h-2.5 rounded-full ${c}`} /> {l}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {!isLineupLocked && isAdminOrStaff && (ev.type === 'mixin' || ev.type === 'tournament') && confirmedParts.length > 0 && (
                        <div className="mb-3 rounded-lg bg-purple-50/60 border border-purple-100 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid={`sort-by-level-hint-${ev.id}`}>
                          <p className="text-[12px] text-purple-700 leading-snug flex-1 min-w-[220px]">
                            {L(
                              'When signup is closed, open "Lineup" to sort by level and drag & drop players between courts.',
                              'Nach Anmeldeschluss "Aufstellung" öffnen, um nach Spielstärke zu sortieren und Spieler per Drag & Drop zwischen den Courts zu verschieben.'
                            )}
                          </p>
                          <Button
                            onClick={() => setLineupModal({ event: ev, mode: 'lineup' })}
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                            data-testid={`sort-by-level-btn-${ev.id}`}
                          >
                            <ListBullets size={14} className="mr-1" />
                            {L('Sort by level →', 'Nach Spielstärke sortieren →')}
                          </Button>
                        </div>
                      )}
                      {courts.length === 0 && (
                        <p className="text-sm italic text-slate-400 mb-3">{L('No confirmed players yet.', 'Noch keine bestätigten Spieler.')}</p>
                      )}
                      {!isLineupLocked ? (
                        /* Signup phase — flat FIFO list. Position number reflects arrival order. */
                        <>
                        {ev.block_pending_count > 0 && (
                          <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800" data-testid={`block-pending-info-${ev.id}`}>
                            {L(
                              `${ev.active_courts} court(s) confirmed. The last ${ev.block_pending_count} player(s) are waiting for court ${ev.active_courts + 1} to fill — ${ev.block_missing} more needed.`,
                              `${ev.active_courts} ${ev.active_courts === 1 ? 'Platz' : 'Plätze'} bestätigt. Die letzten ${ev.block_pending_count} Spieler warten auf Platz ${ev.active_courts + 1} — es ${ev.block_missing === 1 ? 'fehlt noch 1 Spieler' : `fehlen noch ${ev.block_missing} Spieler`}.`
                            )}
                          </div>
                        )}
                        <ul className="rounded-xl border border-slate-200 bg-slate-50/40 overflow-hidden divide-y divide-slate-100" data-testid={`fifo-list-${ev.id}`}>
                          {confirmedParts.map((p, idx) => {
                            const lc = levelColor(p.user_level);
                            return (
                              <li key={p.user_id} className={`flex items-center justify-between px-3 py-2 text-sm ${p.block_pending ? 'bg-amber-50/80' : ''}`} data-testid={`fifo-${ev.id}-${idx + 1}`}>
                                <span className="flex items-center gap-2.5 truncate">
                                  <span className="text-[11px] font-extrabold text-slate-400 tabular-nums w-6 text-right">#{idx + 1}</span>
                                  <span className={`inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-full text-[10px] font-extrabold ${lc.bg} ${lc.text}`} title={`Level ${lc.label}`}>
                                    {lc.label}
                                  </span>
                                  {isAdminOrStaff ? (
                                    <button
                                      type="button"
                                      onClick={() => openParticipantTab(ev, p)}
                                      className="font-semibold truncate text-slate-800 hover:text-emerald-700 hover:underline transition-colors flex items-center gap-1 text-left"
                                      title={L('Open tab (drinks / rackets / …)', 'Konto öffnen (Getränke / Schläger / …)')}
                                      data-testid={`open-tab-fifo-${ev.id}-${p.user_id}`}
                                    >
                                      {p.user_name}<MemberBadge user={p} size={12} />
                                    </button>
                                  ) : (
                                    <strong className="font-semibold truncate text-slate-800 flex items-center gap-1">{p.user_name}<MemberBadge user={p} size={12} /></strong>
                                  )}
                                  {teamBadge(p)}
                                  {isAdminOrStaff && p.open_tab_amount > 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openParticipantTab(ev, p); }}
                                      className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 whitespace-nowrap cursor-pointer transition-colors"
                                      data-testid={`open-tab-badge-${ev.id}-${p.user_id}`}
                                      title={L(`Open tab: CHF ${Number(p.open_tab_amount).toFixed(2)}`,
                                              `Offenes Konto: CHF ${Number(p.open_tab_amount).toFixed(2)}`)}
                                    >
                                      🧾 {Number(p.open_tab_amount).toFixed(2)}
                                    </button>
                                  )}
                                  {isAdminOrStaff && p.open_tab_amount > 0 && p.pay_at_club && !p.paid_at_club && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setSettleFor({ ev, p }); }}
                                      className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap transition-colors"
                                      data-testid={`mixin-settle-btn-${ev.id}-${p.user_id}`}>
                                      {L('Mixin + tab', 'Mixin + Konto kassieren')}
                                    </button>
                                  )}
                                  {isAdminOrStaff && p.pay_at_club && !p.paid_at_club && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); markPaidAtClub(ev, p); }}
                                      className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 whitespace-nowrap cursor-pointer transition-colors"
                                      data-testid={`pay-at-club-open-${ev.id}-${p.user_id}`}
                                      title={L('Click to settle at the desk (cash/card/…)',
                                              'Klick zum Abrechnen an der Rezeption (Bar/Karte/…)')}
                                    >
                                      💰 {L('at club', 'im Club offen')}
                                    </button>
                                  )}
                                  {isAdminOrStaff && p.pay_at_club && p.paid_at_club && (
                                    <span
                                      className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 whitespace-nowrap"
                                      data-testid={`pay-at-club-paid-${ev.id}-${p.user_id}`}
                                      title={L(`Paid at club (${p.pay_at_club_method || ''})`, `Im Club bezahlt (${p.pay_at_club_method || ''})`)}
                                    >
                                      ✓ {L('paid @ club', 'im Club bezahlt')}
                                    </span>
                                  )}
                                  {p.block_pending && (
                                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap" data-testid={`block-pending-badge-${ev.id}-${p.user_id}`}>
                                      {L(`waiting · ${ev.block_missing} missing`, `wartet · ${ev.block_missing} fehlen`)}
                                    </span>
                                  )}
                                  {p.payment_status === 'awaiting_payment' && (
                                    isAdminOrStaff ? (
                                      <button
                                        type="button"
                                        onClick={() => openPaymentInfo(ev, p)}
                                        className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-900 transition-colors whitespace-nowrap cursor-pointer"
                                        data-testid={`payment-badge-${ev.id}-${p.user_id}`}
                                        title={L('Show payment status', 'Zahlungsstatus anzeigen')}
                                      >
                                        {L('pay', 'Zahlung')}
                                      </button>
                                    ) : (
                                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 whitespace-nowrap">{L('pay', 'Zahlung')}</span>
                                    )
                                  )}
                                </span>
                                {isAdminOrStaff && (
                                  <button onClick={() => handleAdminRemove(ev.id, p.user_id, p.user_name)} className="p-1 text-red-400 hover:bg-red-50 rounded" data-testid={`remove-${ev.id}-${p.user_id}`} title={L('Remove', 'Entfernen')}>
                                    <Trash size={12} />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                        </>
                      ) : (
                      <div className="space-y-3">
                        {courts.map((court, ci) => (
                          <div key={`court-${ci}`} className="rounded-xl border border-slate-200 bg-slate-50/40 overflow-hidden" data-testid={`court-${ev.id}-${ci + 1}`}>
                            <div className="flex items-center justify-between bg-purple-50/70 border-b border-purple-100 px-3 py-1.5">
                              <span className="text-xs font-bold uppercase tracking-wider text-purple-700">
                                {L('Court', 'Court')} {ci + 1}
                              </span>
                              <span className="text-[10px] text-purple-500/80 font-semibold">
                                {court.length}/{perCourt}
                              </span>
                            </div>
                            <ul className="divide-y divide-slate-100">
                              {court.map((p, pi) => {
                                const lc = levelColor(p.user_level);
                                return (
                                  <li key={p.user_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`court-${ev.id}-${ci + 1}-player-${pi + 1}`}>
                                    <span className="flex items-center gap-2.5 truncate">
                                      <span className={`inline-flex items-center justify-center min-w-[2.25rem] h-7 px-1.5 rounded-full text-[11px] font-extrabold ${lc.bg} ${lc.text}`} title={`Level ${lc.label}`} data-testid={`level-badge-${p.user_id}`}>
                                        {lc.label}
                                      </span>
                                      <span className="text-[10px] font-semibold text-slate-300 tabular-nums">#{ci * perCourt + pi + 1}</span>
                                      {isAdminOrStaff ? (
                                        <button
                                          type="button"
                                          onClick={() => openParticipantTab(ev, p)}
                                          className="font-semibold truncate text-slate-800 hover:text-emerald-700 hover:underline transition-colors flex items-center gap-1 text-left"
                                          title={L('Open tab (drinks / rackets / …)', 'Konto öffnen (Getränke / Schläger / …)')}
                                          data-testid={`open-tab-court-${ev.id}-${p.user_id}`}
                                        >
                                          {p.user_name}<MemberBadge user={p} size={12} />
                                        </button>
                                      ) : (
                                        <strong className="font-semibold truncate text-slate-800 flex items-center gap-1">{p.user_name}<MemberBadge user={p} size={12} /></strong>
                                      )}
                                      {teamBadge(p)}
                                      {isAdminOrStaff && p.open_tab_amount > 0 && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); openParticipantTab(ev, p); }}
                                          className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 whitespace-nowrap cursor-pointer transition-colors"
                                          data-testid={`open-tab-badge-court-${ev.id}-${p.user_id}`}
                                          title={L(`Open tab: CHF ${Number(p.open_tab_amount).toFixed(2)}`,
                                                  `Offenes Konto: CHF ${Number(p.open_tab_amount).toFixed(2)}`)}
                                        >
                                          🧾 {Number(p.open_tab_amount).toFixed(2)}
                                        </button>
                                      )}
                                      {isAdminOrStaff && p.open_tab_amount > 0 && p.pay_at_club && !p.paid_at_club && (
                                        <button type="button" onClick={(e) => { e.stopPropagation(); setSettleFor({ ev, p }); }}
                                          className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap transition-colors"
                                          data-testid={`mixin-settle-btn-court-${ev.id}-${p.user_id}`}>
                                          {L('Mixin + tab', 'Mixin + Konto kassieren')}
                                        </button>
                                      )}
                                      {isAdminOrStaff && p.pay_at_club && !p.paid_at_club && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); markPaidAtClub(ev, p); }}
                                          className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 whitespace-nowrap cursor-pointer transition-colors"
                                          data-testid={`pay-at-club-open-court-${ev.id}-${p.user_id}`}
                                          title={L('Click to settle at the desk (cash/card/…)',
                                                  'Klick zum Abrechnen an der Rezeption (Bar/Karte/…)')}
                                        >
                                          💰
                                        </button>
                                      )}
                                      {isAdminOrStaff && p.pay_at_club && p.paid_at_club && (
                                        <span
                                          className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 whitespace-nowrap"
                                          title={L(`Paid at club (${p.pay_at_club_method || ''})`, `Im Club bezahlt (${p.pay_at_club_method || ''})`)}
                                        >
                                          ✓
                                        </span>
                                      )}
                                    </span>
                                    {isAdminOrStaff && (
                                      <button onClick={() => handleAdminRemove(ev.id, p.user_id, p.user_name)} className="p-1 text-red-400 hover:bg-red-50 rounded" data-testid={`remove-${ev.id}-${p.user_id}`} title={L('Remove', 'Entfernen')}>
                                        <Trash size={12} />
                                      </button>
                                    )}
                                  </li>
                                );
                              })}
                              {/* Empty seats for the visual structure */}
                              {Array.from({ length: Math.max(0, perCourt - court.length) }).map((_, ei) => (
                                <li key={`empty-${ci}-${ei}`} className="flex items-center px-3 py-2 text-sm text-slate-300 italic">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-400 text-[10px] font-bold mr-2">
                                    {ci * perCourt + court.length + ei + 1}
                                  </span>
                                  {L('open', 'frei')}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      )}

                      {waitlistParts.length > 0 && (
                        <div className="mt-4">
                          {(() => {
                            const per = ev.players_per_court || 4;
                            const rem = confirmedParts.length % per;
                            const canFill = isAdminOrStaff && ev.type === 'mixin' && ev.auto_finalized_at
                              && confirmedParts.length < (ev.max_participants || 32)
                              && (rem > 0 ? rem + waitlistParts.length >= per : waitlistParts.length >= per);
                            return canFill && (
                              <div className="mb-2 p-2 rounded-md bg-orange-50 border border-orange-300 text-orange-800 text-xs font-semibold" data-testid={`waitlist-can-fill-${ev.id}`}>
                                {L(`The waitlist can complete court ${Math.floor(confirmedParts.length / per) + 1} — move players up with the arrow.`,
                                   `Die Warteliste kann Platz ${Math.floor(confirmedParts.length / per) + 1} vervollständigen — Spieler mit dem Pfeil nachrücken lassen.`)}
                              </div>
                            );
                          })()}
                          <h5 className="text-xs uppercase font-bold text-amber-600 mb-2 tracking-wider">{L('Waitlist', 'Warteliste')}</h5>
                          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            {waitlistParts.map((p, idx) => {
                              const lc = levelColor(p.user_level);
                              return (
                                <li key={p.user_id} className="flex items-center justify-between py-1 px-2 rounded-md text-sm bg-amber-50 text-amber-900">
                                  <span className="flex items-center gap-2 truncate">
                                    <span className={`inline-flex items-center justify-center min-w-[2rem] h-5 px-1 rounded-full text-[10px] font-extrabold ${lc.bg} ${lc.text}`} title={`Level ${lc.label}`}>
                                      {lc.label}
                                    </span>
                                    <span className="text-amber-500 text-[10px] font-semibold">W{idx + 1}</span>
                                    <strong className="font-semibold truncate flex items-center gap-1">{p.user_name}<MemberBadge user={p} size={11} /></strong>
                                    {isAdminOrStaff && p.needs_refund_action && (
                                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700 whitespace-nowrap" data-testid={`needs-refund-${ev.id}-${p.user_id}`} title={L('Paid but court not completed at T-2h — refund needed', 'Bezahlt, aber Platz bei T-2h nicht voll — Rückerstattung nötig')}>
                                        {L('refund needed', 'Rückerstattung nötig')}
                                      </span>
                                    )}
                                  </span>
                                  {isAdminOrStaff && (
                                    <span className="flex items-center">
                                    {confirmedParts.length < (ev.max_participants || 32) && (
                                      <button onClick={() => handleWaitlistPromote(ev.id, p.user_id, p.user_name)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                        title={L('Move up', 'Nachrücken')} data-testid={`waitlist-promote-${ev.id}-${p.user_id}`}>
                                        <ArrowUp size={12} weight="bold" />
                                      </button>
                                    )}
                                    <button onClick={() => handleAdminRemove(ev.id, p.user_id, p.user_name)} className="p-1 text-red-400 hover:bg-red-50 rounded" title={L('Remove', 'Entfernen')}>
                                      <Trash size={12} />
                                    </button>
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(o) => { if (!o) { setShowModal(false); setEditingEvent(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="event-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingEvent ? L('Edit event', 'Event bearbeiten') : L('New event', 'Neues Event')}
            </DialogTitle>
            <DialogDescription>{L('Mixin / Tournament / Social', 'Mixin / Turnier / Social')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="label-text">{L('Type', 'Typ')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {['mixin', 'tournament', 'social'].map(t => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, type: t })}
                    className={`p-2 border rounded-xl text-sm font-semibold capitalize transition-all ${form.type === t ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    data-testid={`event-type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Title', 'Titel')} *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={L('Thursday Evening', 'Thursday Evening')} className="bg-slate-50 border-slate-200" data-testid="event-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-text">{L('Date', 'Datum')} *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-date" />
              </div>
              <div className="space-y-2">
                <Label className="label-text">{L('Start', 'Start')} *</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-start" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Duration min', 'Dauer Min.')}</Label>
                <Input type="number" min="30" step="30" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-duration" />
              </div>
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Courts', 'Courts')}</Label>
                <Input type="number" min="1" max="8" value={form.num_courts} onChange={(e) => setForm({ ...form, num_courts: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-courts" />
              </div>
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Max', 'Max')}</Label>
                <Input type="number" min="1" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-max" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Players/court', 'Spieler/Court')}</Label>
                <Input type="number" min="1" max="8" value={form.players_per_court} onChange={(e) => setForm({ ...form, players_per_court: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-players-per-court" />
              </div>
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Cancel cutoff (h)', 'Abmeldung bis (h)')}</Label>
                <Input type="number" min="0" max="48" value={form.cancellation_cutoff_hours} onChange={(e) => setForm({ ...form, cancellation_cutoff_hours: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-cutoff" />
              </div>
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Normal price', 'Preis normal')}</Label>
                <Input type="number" step="5" min="0" value={form.price_per_player} onChange={(e) => setForm({ ...form, price_per_player: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-price" />
              </div>
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Member price (blue tick)', 'Preis Members (blauer Haken)')}</Label>
                <Input type="number" step="1" min="0" value={form.member_price} placeholder={L('= normal', '= normal')} onChange={(e) => setForm({ ...form, member_price: e.target.value })} className="bg-slate-50 border-slate-200" data-testid="event-member-price" />
              </div>
            </div>

            {/* Level-gate (Oct 2026): optional min/max player level. Blank =
                no restriction. Example: "Green only" = min 6.0, max blank.
                Backend blocks signups outside the band with a clear error. */}
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
              <div className="space-y-1">
                <Label className="label-text text-xs">{L('Min level (≥)', 'Min Level (≥)')}</Label>
                <Input
                  type="number" step="0.1" min="1" max="7.9"
                  placeholder={L('e.g. 6.0 — green only', 'z.B. 6.0 — nur Grün')}
                  value={form.min_level}
                  onChange={(e) => setForm({ ...form, min_level: e.target.value })}
                  className="bg-white border-emerald-200"
                  data-testid="event-min-level"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-text text-xs">{L('Max level (≤)', 'Max Level (≤)')}</Label>
                <Input
                  type="number" step="0.1" min="1" max="7.9"
                  placeholder={L('e.g. 3.9 — advanced only', 'z.B. 3.9 — nur Fortgeschritten')}
                  value={form.max_level}
                  onChange={(e) => setForm({ ...form, max_level: e.target.value })}
                  className="bg-white border-emerald-200"
                  data-testid="event-max-level"
                />
              </div>
              <p className="col-span-2 text-[11px] text-emerald-800 leading-snug">
                {L(
                  'Leave both blank for no level restriction. Players outside the range (and their partner) are blocked at signup.',
                  'Beide leer lassen für keine Level-Einschränkung. Spieler außerhalb (und ihr Partner) werden bei Anmeldung blockiert.',
                )}
              </p>
            </div>

            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-indigo-200 bg-indigo-50/50 cursor-pointer" data-testid="event-weekly-toggle">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-indigo-600"
                checked={!!form.weekly_recurring}
                onChange={(e) => setForm({ ...form, weekly_recurring: e.target.checked })}
              />
              <span className="text-sm">
                <span className="font-bold text-indigo-700 block">{L('Weekly recurring', 'Wöchentlich wiederkehrend')}</span>
                <span className="text-xs text-indigo-600/80">
                  {L('Auto-creates one edition per week. Choose an end date below (default: 11 weeks).',
                    'Erstellt automatisch eine Edition pro Woche. Wähle unten ein End-Datum (Standard: 11 Wochen).')}
                </span>
              </span>
            </label>

            {form.weekly_recurring && (
              <div className="space-y-2 p-3 rounded-xl border border-indigo-200 bg-white">
                <Label className="label-text flex items-center gap-1.5 text-indigo-800">
                  <Calendar size={14} weight="fill" /> {L('Series ends on', 'Serie endet am')}
                </Label>
                <Input
                  type="date"
                  value={form.weekly_end_date || ''}
                  onChange={(e) => setForm({ ...form, weekly_end_date: e.target.value })}
                  min={form.date || undefined}
                  className="bg-white border-indigo-200"
                  data-testid="event-weekly-end-date"
                />
                <p className="text-[11px] text-indigo-700/80">
                  {L('Last date to include. Leave empty for the default 12 weeks (~3 months).',
                    'Letztes Datum in der Serie. Leer lassen = 12 Wochen (~3 Monate).')}
                </p>
              </div>
            )}

            <div className="space-y-2 p-3 rounded-xl border border-emerald-200 bg-emerald-50/50">
              <Label className="label-text flex items-center gap-1.5 text-emerald-800">
                <Lock size={14} weight="fill" /> {L('Signup opens each week on', 'Anmeldung öffnet jede Woche am')}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.signup_opens_weekday}
                  onChange={(e) => setForm({ ...form, signup_opens_weekday: e.target.value })}
                  className="bg-white border border-emerald-200 rounded-md px-3 py-2 text-sm"
                  data-testid="event-signup-opens-weekday"
                >
                  <option value="">{L('— Choose weekday —', '— Wochentag wählen —')}</option>
                  <option value="0">{L('Monday', 'Montag')}</option>
                  <option value="1">{L('Tuesday', 'Dienstag')}</option>
                  <option value="2">{L('Wednesday', 'Mittwoch')}</option>
                  <option value="3">{L('Thursday', 'Donnerstag')}</option>
                  <option value="4">{L('Friday', 'Freitag')}</option>
                  <option value="5">{L('Saturday', 'Samstag')}</option>
                  <option value="6">{L('Sunday', 'Sonntag')}</option>
                </select>
                <Input
                  type="time"
                  value={form.signup_opens_time || ''}
                  onChange={(e) => setForm({ ...form, signup_opens_time: e.target.value })}
                  className="bg-white border-emerald-200"
                  data-testid="event-signup-opens-time"
                  placeholder="20:00"
                />
              </div>
              <p className="text-[11px] text-emerald-700/80 leading-relaxed">
                {L(
                  'Example: "Monday 20:00" → signup opens each Monday 20:00 in the same week as the event. If the picked weekday is the SAME as the event weekday (e.g. Sunday event with "Sunday 11:00"), signup opens the PREVIOUS Sunday — so participants always get a full week\'s notice. Leave both empty to use the manual override below.',
                  'Beispiel: "Montag 20:00" → Anmeldung öffnet jeden Montag 20:00 in derselben Woche wie das Event. Wenn der gewählte Wochentag GLEICH wie der Event-Wochentag ist (z.B. Sonntag-Event mit "Sonntag 11:00"), öffnet die Anmeldung am VORHERIGEN Sonntag — Teilnehmer haben also immer eine volle Woche Vorlauf. Beide leer lassen, um das manuelle Override unten zu nutzen.',
                )}
              </p>
            </div>

            <div className="space-y-2 p-3 rounded-xl border border-amber-200 bg-amber-50/50">
              <Label className="label-text flex items-center gap-1.5 text-amber-800">
                <Lock size={14} weight="fill" /> {L('Manual override — signup opens at', 'Manuelles Override — Anmeldung öffnet am')}
              </Label>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <Input
                  type="datetime-local"
                  value={form.signup_opens_at || ''}
                  onChange={(e) => setForm({ ...form, signup_opens_at: e.target.value })}
                  className="bg-white border-amber-200 flex-1"
                  disabled={form.signup_opens_weekday !== '' && !!form.signup_opens_time}
                  data-testid="event-signup-opens-at"
                />
                {form.signup_opens_at && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, signup_opens_at: '' })}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs h-9"
                    data-testid="event-signup-opens-clear"
                  >
                    {L('Open immediately', 'Sofort öffnen')}
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-amber-700/80 leading-relaxed">
                {(form.signup_opens_weekday !== '' && form.signup_opens_time)
                  ? L('Ignored — a weekly rule is set above.', 'Wird ignoriert — Wochenregel oben aktiv.')
                  : (form.weekly_recurring
                      ? L('Optional override for the FIRST event only. Follow-up weeks open 1h after the previous edition ends.',
                          'Optionales Override nur für das ERSTE Event. Folgewochen öffnen 1h nach vorheriger Edition.')
                      : L('Leave empty to open signup immediately. Set a date/time to delay signup opening.',
                          'Leer lassen für sofortige Anmeldung. Datum/Uhrzeit setzen, um die Anmeldung zu verzögern.'))}
              </p>
            </div>

            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-sky-200 bg-sky-50/50 cursor-pointer" data-testid="event-members-only-toggle">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-sky-600"
                checked={!!form.members_only}
                onChange={(e) => setForm({ ...form, members_only: e.target.checked })}
              />
              <span className="text-sm">
                <span className="font-bold text-sky-700 flex items-center gap-1.5">
                  <SealCheck size={14} weight="fill" /> {L('Members only', 'Nur für Mitglieder')}
                </span>
                <span className="text-xs text-sky-600/80 block mt-0.5">
                  {L('Non-members can see the event but cannot sign up. Members also get 1h early signup access on every event.',
                    'Nicht-Mitglieder sehen das Event, können sich aber nicht anmelden. Mitglieder können sich generell 1h früher anmelden.')}
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-emerald-200 bg-emerald-50/50 cursor-pointer" data-testid="event-pay-at-club-only-toggle">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-emerald-600"
                checked={!!form.pay_at_club_only}
                onChange={(e) => setForm({ ...form, pay_at_club_only: e.target.checked })}
              />
              <span className="text-sm">
                <span className="font-bold text-emerald-700 flex items-center gap-1.5">
                  <MapPin size={14} weight="fill" /> {L('Pay at club only', 'Nur Bezahlung im Club')}
                </span>
                <span className="text-xs text-emerald-700/80 block mt-0.5">
                  {L('No online payment. Signup reserves the spot, reception collects on arrival.',
                    'Keine Online-Zahlung. Die Anmeldung reserviert den Platz, der Empfang kassiert vor Ort.')}
                </span>
              </span>
            </label>

            {/* Signup mode: solo / partner / both */}
            <div className="space-y-2" data-testid="event-signup-mode-block">
              <Label className="label-text flex items-center gap-1.5">
                <UsersThree size={14} /> {L('Signup mode', 'Anmelde-Modus')}
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'solo', title: L('Solo only', 'Nur solo'), sub: L('Individual signup', 'Einzelanmeldung') },
                  { key: 'partner', title: L('Partner required', 'Nur mit Partner'), sub: L('Must sign up as team', 'Anmeldung nur im Team') },
                  { key: 'both', title: L('Both allowed', 'Beides erlaubt'), sub: L('Solo or with a partner', 'Solo oder mit Partner') },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setForm({ ...form, signup_mode: opt.key })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      form.signup_mode === opt.key
                        ? 'border-purple-500 bg-purple-50 shadow-sm'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`signup-mode-${opt.key}`}
                  >
                    <div className="text-xs font-bold text-slate-900">{opt.title}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{opt.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-text">{L('Description', 'Beschreibung')}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={L('e.g. all levels welcome, drinks after match', 'z.B. Alle Spielstärken willkommen, Drinks danach')} className="bg-slate-50 border-slate-200 min-h-[80px]" data-testid="event-description" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowModal(false); setEditingEvent(null); }} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
              <Button onClick={handleSubmit} className="flex-1 bg-purple-500 text-white hover:bg-purple-600 font-bold" data-testid="event-save">
                {editingEvent ? L('Save', 'Speichern') : L('Create', 'Anlegen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: add player modal */}
      <Dialog open={!!showAddPlayer} onOpenChange={(o) => { if (!o) { setShowAddPlayer(null); setSearchQuery(''); setSearchResults([]); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="add-player-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Add player', 'Spieler hinzufügen')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={L('Search by name or email…', 'Nach Name oder E-Mail suchen…')} className="bg-slate-50 border-slate-200" autoFocus data-testid="search-player" />
            {searchResults.length > 0 && (
              <div className="border border-slate-200 rounded-xl max-h-64 overflow-y-auto">
                {searchResults.map(u => (
                  <button key={u.id} onClick={() => handleAdminAdd(showAddPlayer, u.id)}
                    className="w-full text-left px-4 py-2 hover:bg-purple-50 border-b border-slate-100 last:border-b-0 transition-colors"
                    data-testid={`pick-player-${u.id}`}>
                    <p className="font-semibold text-slate-800">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}{u.player_level != null && <> · L{Number(u.player_level).toFixed(2)}</>}</p>
                  </button>
                ))}
              </div>
            )}
            {searchQuery.trim().length >= 2 && searchResults.length === 0 && (
              <p className="text-sm text-slate-400 italic">{L('No matching customers', 'Keine passenden Kunden')}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Late-cancel confirmation dialog (no refund within cutoff) */}
      <Dialog open={!!lateFeeConfirm} onOpenChange={(o) => { if (!o) setLateFeeConfirm(null); }}>
        <DialogContent className="bg-white border-amber-200 text-slate-900 max-w-md" data-testid="late-fee-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-amber-700">
              <Warning size={22} weight="fill" /> {L('Late cancellation — no refund', 'Späte Abmeldung — keine Rückerstattung')}
            </DialogTitle>
            <DialogDescription>
              {lateFeeConfirm && L(
                `You are unregistering ${lateFeeConfirm.hoursUntilStart}h before start (cutoff: ${lateFeeConfirm.cutoffHours}h).`,
                `Du meldest dich ${lateFeeConfirm.hoursUntilStart}h vor Start ab (Frist: ${lateFeeConfirm.cutoffHours}h).`
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
              <p className="text-sm text-amber-900 font-semibold">
                {L(
                  `Your ${lateFeeConfirm?.feeAmount} CHF payment will not be refunded.`,
                  `Deine ${lateFeeConfirm?.feeAmount} CHF werden nicht erstattet.`
                )}
              </p>
              <p className="text-xs text-amber-800">
                {L(
                  'Your spot will be offered free of charge to the next signup or waitlist player, since the seat is already paid for.',
                  'Dein Platz wird dem nächsten Spieler bzw. der Warteliste kostenlos angeboten, da bereits bezahlt.'
                )}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLateFeeConfirm(null)} className="border-slate-200" data-testid="late-fee-cancel">
              {L('Keep my registration', 'Angemeldet bleiben')}
            </Button>
            <Button onClick={() => performCancel(lateFeeConfirm.event, true)} className="bg-amber-500 text-white hover:bg-amber-600 font-bold" data-testid="late-fee-confirm">
              {L('Cancel anyway', 'Trotzdem abmelden')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generic confirm dialog (delete event / delete series / remove player / unregister) */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="confirm-delete-modal">
          {confirmDelete?.type === 'series-or-one' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-700">
                  <Trash size={20} weight="fill" /> {L('Delete weekly series?', 'Wöchentliche Serie löschen?')}
                </DialogTitle>
                <DialogDescription>
                  {L('This is a recurring weekly event. Choose what to delete.', 'Dies ist ein wöchentlich wiederkehrendes Event. Wähle, was gelöscht werden soll.')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <button
                  onClick={() => performDeleteEvent(confirmDelete.ev, false)}
                  className="w-full text-left bg-amber-50 border border-amber-200 rounded-xl p-4 hover:border-amber-400 hover:bg-amber-100 transition-colors"
                  data-testid="confirm-delete-only-this">
                  <p className="font-bold text-amber-800">{L('Delete only this week', 'Nur diese Woche löschen')}</p>
                  <p className="text-xs text-amber-700 mt-0.5">{confirmDelete.title} · {confirmDelete.date}</p>
                </button>
                <button
                  onClick={() => performDeleteEvent(confirmDelete.ev, true)}
                  className="w-full text-left bg-red-50 border border-red-200 rounded-xl p-4 hover:border-red-400 hover:bg-red-100 transition-colors"
                  data-testid="confirm-delete-series">
                  <p className="font-bold text-red-800">{L('Delete entire series (all weeks)', 'Ganze Serie löschen (alle Wochen)')}</p>
                  <p className="text-xs text-red-700 mt-0.5">{L('All upcoming weeks will be removed', 'Alle kommenden Wochen werden entfernt')}</p>
                </button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-200" data-testid="confirm-cancel">
                  {L('Cancel', 'Abbrechen')}
                </Button>
              </DialogFooter>
            </>
          )}
          {confirmDelete?.type === 'event' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-700">
                  <Trash size={20} weight="fill" /> {L('Delete event?', 'Event löschen?')}
                </DialogTitle>
                <DialogDescription>
                  {confirmDelete.title} · {confirmDelete.date}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-200" data-testid="confirm-cancel">
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button onClick={() => performDeleteEvent(confirmDelete.ev, false)} className="bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-delete">
                  {L('Delete', 'Löschen')}
                </Button>
              </DialogFooter>
            </>
          )}
          {confirmDelete?.type === 'player' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-700">
                  <Trash size={20} weight="fill" /> {L('Remove player?', 'Spieler entfernen?')}
                </DialogTitle>
                <DialogDescription>{confirmDelete.userName}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-200" data-testid="confirm-cancel">
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button onClick={() => performAdminRemove(confirmDelete.eventId, confirmDelete.userId)} className="bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-remove-player">
                  {L('Remove', 'Entfernen')}
                </Button>
              </DialogFooter>
            </>
          )}
          {confirmDelete?.type === 'unregister' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">{L('Unregister?', 'Abmelden?')}</DialogTitle>
                <DialogDescription>
                  {L('Cancel your registration for this event?', 'Deine Anmeldung für dieses Event stornieren?')}
                  {confirmDelete.ev?.signup_mode === 'partner' && (
                    <span className="block mt-2 font-semibold text-red-600" data-testid="unregister-partner-warning">
                      {L('Your partner will be removed from this event as well.', 'Deine Partnerin / dein Partner wird ebenfalls abgemeldet.')}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-200" data-testid="confirm-cancel">
                  {L('Stay signed up', 'Angemeldet bleiben')}
                </Button>
                <Button onClick={() => performUnregister(confirmDelete.ev)} className="bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-unregister">
                  {L('Unregister', 'Abmelden')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ MIXIN LINEUP / RESULTS BOARD ============ */}
      {lineupModal && (
        <MixinLineupBoard
          event={lineupModal.event}
          mode={lineupModal.mode}
          open={!!lineupModal}
          onClose={() => setLineupModal(null)}
          onUpdated={() => loadEvents()}
        />
      )}

      {/* ============ PARTICIPANT PAYMENT INFO ============ */}
      <Dialog open={!!paymentInfo} onOpenChange={(o) => { if (!o) setPaymentInfo(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="payment-info-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Warning size={20} className="text-amber-500" weight="fill" />
              {L('Payment pending', 'Zahlung ausstehend')}
            </DialogTitle>
            <DialogDescription>
              {paymentInfo?.participant?.user_name} · {paymentInfo?.event?.title}
            </DialogDescription>
          </DialogHeader>
          {paymentInfo?.loading ? (
            <div className="py-6 text-center text-slate-400 text-sm">{L('Loading…', 'Laden…')}</div>
          ) : paymentInfo?.error ? (
            <div className="py-4 px-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{paymentInfo.error}</div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                <Row label={L('Participant status', 'Teilnehmer-Status')} value={paymentInfo?.participant?.payment_status || '—'} testid="pi-part-status" />
                <Row label={L('Signed up at', 'Angemeldet am')} value={fmtDate(paymentInfo?.participant?.signed_up_at, language)} testid="pi-signed-up" />
                {paymentInfo?.transaction ? (
                  <>
                    <Row label={L('Amount', 'Betrag')} value={`${paymentInfo.transaction.amount} ${paymentInfo.transaction.currency}`} testid="pi-amount" />
                    <Row label={L('Datatrans status', 'Datatrans-Status')} value={paymentInfo.transaction.status || '—'} testid="pi-tx-status" />
                    <Row label={L('Datatrans state', 'Datatrans-State')} value={paymentInfo.transaction.datatrans_state || '—'} testid="pi-tx-state" />
                    <Row label={L('Datatrans ID', 'Datatrans-ID')} value={paymentInfo.transaction.datatrans_transaction_id || '—'} testid="pi-tx-id" mono />
                    <Row label={L('Started at', 'Gestartet am')} value={fmtDate(paymentInfo.transaction.created_at, language)} testid="pi-tx-created" />
                    {paymentInfo.transaction.paid_at && (
                      <Row label={L('Paid at', 'Bezahlt am')} value={fmtDate(paymentInfo.transaction.paid_at, language)} testid="pi-tx-paid" />
                    )}
                    {(paymentInfo.transaction.refunds || []).length > 0 && (
                      <Row label={L('Refunds', 'Rückerstattungen')} value={`${paymentInfo.transaction.refunds.length}`} testid="pi-tx-refunds" />
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 text-slate-500 italic">
                    {L('No Datatrans transaction found for this user on this event yet.', 'Noch keine Datatrans-Transaktion für diesen Benutzer bei diesem Event.')}
                  </div>
                )}
              </div>
              <p className="text-[12px] text-slate-500 leading-snug">
                {L(
                  'Reconcile polls Datatrans directly and updates the payment status. Use this when the customer paid but the badge is still amber.',
                  'Reconcile fragt Datatrans direkt ab und aktualisiert den Zahlungsstatus. Nutze dies, wenn der Kunde bezahlt hat, das Badge aber noch gelb ist.'
                )}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPaymentInfo(null)} className="border-slate-200" data-testid="payment-info-close">
              {L('Close', 'Schließen')}
            </Button>
            {paymentInfo?.transaction?.datatrans_transaction_id && (
              <Button
                onClick={runReconcile}
                disabled={reconciling}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                data-testid="payment-info-reconcile"
              >
                {reconciling ? L('Reconciling…', 'Abgleich läuft…') : L('Reconcile now', 'Jetzt abgleichen')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner-signup dialog (for signup_mode = 'partner' or 'both') */}
      <Dialog open={!!partnerSignup} onOpenChange={(o) => { if (!o) { setPartnerSignup(null); setPartnerQuery(''); setPartnerResults([]); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="partner-signup-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-black flex items-center gap-2">
              <UsersThree size={20} className="text-purple-600" />
              {L('Sign up for', 'Anmeldung zu')} — {partnerSignup?.event?.title}
            </DialogTitle>
          </DialogHeader>
          {partnerSignup && (
            <div className="space-y-4">
              {/* Mode picker: only for 'both' */}
              {partnerSignup.mode === 'both' && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPartnerSignup({ ...partnerSignup, partner: 'solo' })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      partnerSignup.partner === 'solo'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="partner-choose-solo"
                  >
                    <div className="text-sm font-bold">{L('Sign up alone', 'Alleine anmelden')}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{L('Just me', 'Nur ich')}</div>
                  </button>
                  <button
                    onClick={() => setPartnerSignup({ ...partnerSignup, partner: null })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      partnerSignup.partner && partnerSignup.partner !== 'solo'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="partner-choose-team"
                  >
                    <div className="text-sm font-bold">{L('With a partner', 'Mit Partner')}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{L('Team of 2', 'Zu zweit')}</div>
                  </button>
                </div>
              )}

              {/* Partner search (hidden when user picked solo) */}
              {partnerSignup.partner !== 'solo' && (
                <div className="space-y-2" data-testid="partner-picker-block">
                  <Label className="label-text">{L('Choose your partner', 'Wähle deinen Partner')}</Label>
                  {partnerSignup.partner && partnerSignup.partner !== 'solo' ? (
                    <div className="flex items-center justify-between rounded-xl border border-purple-300 bg-purple-50 px-3 py-2" data-testid="partner-selected">
                      <div>
                        <div className="text-sm font-bold text-slate-900">{partnerSignup.partner.name}</div>
                        {partnerSignup.partner.player_level && (
                          <div className="text-[11px] text-slate-500">{L('Level', 'Level')} {partnerSignup.partner.player_level.toFixed(2)}</div>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setPartnerSignup({ ...partnerSignup, partner: null })} data-testid="partner-change">
                        {L('Change', 'Ändern')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={partnerQuery}
                          onChange={(e) => setPartnerQuery(e.target.value)}
                          placeholder={L('Search partner by name…', 'Partner nach Name suchen…')}
                          className="pl-9"
                          autoFocus
                          data-testid="partner-search-input"
                        />
                      </div>
                      {partnerQuery.trim().length < 2 ? (
                        <p className="text-xs text-slate-400">{L('Type at least 2 characters', 'Mindestens 2 Zeichen eingeben')}</p>
                      ) : partnerSearching ? (
                        <p className="text-xs text-slate-400">{L('Searching…', 'Suche…')}</p>
                      ) : partnerResults.length === 0 ? (
                        <p className="text-xs text-slate-500 py-2">
                          {L('No matches. Your partner must have an activated account.', 'Keine Treffer. Dein Partner muss ein aktiviertes Konto haben.')}
                        </p>
                      ) : (
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                          {partnerResults.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => setPartnerSignup({ ...partnerSignup, partner: u })}
                              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between"
                              data-testid={`partner-result-${u.id}`}
                            >
                              <div>
                                <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                                  {u.player_level && <span>{L('Level', 'Level')} {u.player_level.toFixed(2)}</span>}
                                  {u.is_member && <span className="text-sky-600 font-semibold">{L('Member', 'Member')}</span>}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Pay-for-both toggle — only when paid event AND team selected
                  AND not pay-at-club (in club mode nobody pays online). */}
              {!partnerSignup.payAtClub && (partnerSignup.event?.price_per_player || 0) > 0 && partnerSignup.partner && partnerSignup.partner !== 'solo' && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="pay-for-both-block">
                  <div className="text-xs font-bold text-emerald-800 uppercase">{L('Payment', 'Zahlung')}</div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 accent-emerald-600"
                      checked={!!partnerSignup.payForBoth}
                      onChange={(e) => setPartnerSignup({ ...partnerSignup, payForBoth: e.target.checked })}
                      data-testid="pay-for-both-toggle"
                    />
                    <span className="text-sm text-slate-800">
                      <strong>{L("I'll pay for both", 'Ich zahle für beide')}</strong>
                      {' — '}
                      {(priceFor(partnerSignup.event, user?.is_member) + priceFor(partnerSignup.event, partnerSignup.partner?.is_member)).toFixed(2)} CHF
                    </span>
                  </label>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    {partnerSignup.payForBoth
                      ? L('You pay for both. Once your payment is complete, both of you are confirmed.', 'Du zahlst für beide. Sobald deine Zahlung durch ist, seid ihr beide bestätigt.')
                      : L('Each of you pays separately. As soon as ONE of you pays, both are confirmed.', 'Jeder zahlt separat. Sobald EINER von euch bezahlt, seid ihr beide bestätigt.')}
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPartnerSignup(null)} className="flex-1" data-testid="partner-signup-cancel">
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button
                  disabled={partnerSignup.mode === 'partner' && (!partnerSignup.partner || partnerSignup.partner === 'solo')}
                  onClick={() => {
                    const partner = partnerSignup.partner;
                    const commonOpts = {
                      skipPartnerCheck: true,
                      payAtClub: !!partnerSignup.payAtClub,   // preserve pay-at-club choice
                    };
                    if (partner && partner !== 'solo') {
                      handleSignup(partnerSignup.event, {
                        ...commonOpts,
                        partnerId: partner.id,
                        // pay_for_both only matters for card checkout; in
                        // pay-at-club mode both are individually reserved
                        // as "confirmed" without payment anyway.
                        payForBoth: !partnerSignup.payAtClub && !!partnerSignup.payForBoth,
                      });
                    } else {
                      // 'both' mode + user chose solo
                      handleSignup(partnerSignup.event, commonOpts);
                    }
                  }}
                  className={`flex-1 font-bold text-white ${partnerSignup.payAtClub ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                  data-testid="partner-signup-confirm"
                >
                  {partnerSignup.payAtClub
                    ? <><MapPin size={14} className="mr-1 inline" /> {L('Confirm · Pay at club', 'Bestätigen · Im Club zahlen')}</>
                    : L('Confirm signup', 'Anmeldung bestätigen')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Compact key/value row for the payment-info dialog. */
const priceFor = (ev, isMember) => (isMember && ev?.member_price != null ? Number(ev.member_price) : Number(ev?.price_per_player || 0));

const Row = ({ label, value, testid, mono }) => (
  <div className="grid grid-cols-[130px_1fr] px-3 py-2 gap-2" data-testid={testid}>
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-800 break-all ${mono ? 'font-mono text-[11px]' : 'font-semibold'}`}>{value}</span>
  </div>
);

/** Live countdown to `expiresAt`. Turns red when <30 min remain, shows
 *  "expired" (crossed-out) once we pass it — the sweeper will remove the
 *  signup within 60s of that moment. */
const PaymentCountdown = ({ expiresAt, language, testid }) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(expiresAt).getTime();
  const diff = target - now;
  const de = language === 'de';
  if (isNaN(target)) return null;
  if (diff <= 0) {
    return (
      <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wide text-red-700" data-testid={testid}>
        {de ? 'Frist abgelaufen — wird gleich automatisch storniert' : 'Hold expired — will be auto-cancelled shortly'}
      </p>
    );
  }
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  const urgent = mins < 30;
  const label = hours > 0
    ? `${hours}h ${remainMins}min`
    : `${remainMins}min`;
  return (
    <p className={`mt-1.5 text-[11px] font-bold tracking-wide ${urgent ? 'text-red-700 animate-pulse' : 'text-amber-800'}`} data-testid={testid}>
      {de ? '⏳ Verbleibende Zeit zum Bezahlen:' : '⏳ Time left to pay:'} <span className="font-extrabold tabular-nums">{label}</span>
    </p>
  );
};

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export default Community;
